import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import confetti from "canvas-confetti";

import SoundEngine from '../engine/sounds';
import { UNIQUE_PLAYERS, PLAYERS } from '../data/players';
import { generatePersonalTierSheet } from '../data/tiers';
import { TEAM_NAMES, ARCHETYPES, MODIFIERS, DEFAULT_AI_ARCHETYPES, MOCK_TEAM_NAMES, MOCK_ROSTER_SIZE, SPEED_SETTINGS } from '../data/archetypes';
import { calcAiMaxBidV2, calcAiOpeningBid, pickAiNomination } from '../engine/auction';
import { buildDraftTeams, opponentSeats, simulateDraftSync, buildRosterSlots, applyValueRecognitionFloor, applyKeepers, _roomRule } from '../engine/simulation';
import { rosterablePositions, starterRequirements } from '../utils/rosterSlotFill';
import { tierForPlayer, tierMidDollars } from '../utils/roomTemp';
import { filterPlayerList } from '../utils/filterPlayerList';
import { fitMemberProfiles } from '../utils/leagueMemberProfiles';
import { captureMockDraftStarted, captureMockDraftCompleted, captureNominationFallback, captureNominationWatchdog } from '../utils/mockAnalytics';
import { leagueFormatLabel } from '../utils/leagueFormat';
import { marketAnchorPresetFor } from '../utils/marketAnchor';
import posthog from 'posthog-js';
import { isInternalUser } from '../utils/analyticsGate';
import { createDraftLog } from '../utils/draftLogger';
import { activeDialsTag } from '../utils/devDials';
import { isDefaultLeague } from '../utils/leagueDefaults';
import { selectTailSales, recordedSalePicks } from '../utils/tailSales';
import { sanitizeKeepers, hasKeepers, resolveKeepers } from '../utils/keepers';
import pkg from '../../package.json';

const APP_VERSION = pkg.version;

// ── Per-league opponent configs ─────────────────────────────────────────────
// Opponent archetypes/modifiers are league-specific by nature ("my real
// opponents"), so they're stored under a per-league key. The pre-league global
// key ("auctionArchitect_teamConfigs") is still read as a seed for any league
// that has no configs yet — it is COPIED FORWARD, never deleted or overwritten,
// so older builds (and leagues seeded later) keep working.
// How often the sold phase re-checks whether the outstanding AI bid has landed.
// Short enough to be imperceptible against the 600ms sold animation.
const BID_PENDING_RECHECK_MS = 50;

// Ceiling on nomination_fallback events per draft. High enough to characterize a
// stall (which recovery tiers fired, at which picks, with which pools), low
// enough that a draft where every remaining pick falls back can't flood PostHog.
const NOM_FALLBACK_EVENT_CAP = 25;
// Nomination watchdog delay. Must exceed any legitimate time in "nominating":
// AI nomination delays top out around 4s, and a user seat re-arms the watchdog
// on every countdown tick, so 45s is far outside normal play in every speed.
const NOM_WATCHDOG_MS = 45000;

const LEGACY_CONFIGS_KEY = "auctionArchitect_teamConfigs";
const cfgKey = lg => "auctionArchitect_teamConfigs_" + (lg || "default");

// Build a full config map from a saved blob (or defaults when there is none).
// Reserved "__"-prefixed keys (e.g. "__user", the user's Auto-Bid profile) are
// carried through verbatim — they aren't in TEAM_NAMES and would be dropped.
function sanitizeTeamConfigs(saved) {
  const configs = {};
  if (saved && typeof saved === "object") {
    Object.keys(saved).forEach(k => { if (k.startsWith("__")) configs[k] = saved[k]; });
  }
  TEAM_NAMES.forEach((name, i) => {
    const s = (saved && typeof saved === "object" && saved[name]) || {};
    configs[name] = {
      customName: s.customName || "",
      archetype: ARCHETYPES[s.archetype] ? s.archetype : (DEFAULT_AI_ARCHETYPES[i] || "Balanced"),
      modifiers: Array.isArray(s.modifiers) ? s.modifiers.filter(m => MODIFIERS[m]) : [],
      favoriteTeam: s.favoriteTeam || null,
      overpay: s.overpay ?? null,
      // This whitelist is strict: a key it does not name works all session and
      // is silently gone after a reload — the failure mode that looks like the
      // feature working. `keepers` gets the same defensive load `modifiers`
      // does one line up; sanitizeKeepers drops anything that isn't
      // {name: string, pos: string, price: positive int}.
      //
      // The user's own seat needs no line here: it lives under "__user", and
      // "__"-prefixed keys are carried through verbatim at :53.
      keepers: sanitizeKeepers(s.keepers),
    };
  });
  return configs;
}

// Seat the AI opponents with real leaguemate names.
//
// This writes ONLY `customName`, the display layer that already exists on a
// team config (simulation.js reads it for the seat's display name and falls
// back to the config key). The config KEYS stay TEAM_NAMES, which is
// load-bearing: buildDraftTeams derives its owner list from TEAM_NAMES and
// looks up teamConfigs by those keys, so renaming keys would orphan every
// config and drop the whole room to Balanced — the name-pool bug.
//
// Only empty customNames are filled: a name the user typed themselves outranks
// anything imported. Seats past the end of the member list keep "" and fall
// back to their default name, which is what makes members < seats safe.
// Returns the SAME object when there's nothing to do, so callers can use it as
// a cheap no-op guard.
// opponentSeats MOVED to engine/simulation.js, next to buildDraftTeams — the
// function whose pool it has to equal. It is imported above and used below
// unchanged. Not re-exported from here on purpose: one home means one import
// path, and the reason it moved is that a setup screen needed it without
// dragging this hook (and the sound engine) along.

// `selfDisplayName` is the manager the user is sitting as; they're removed from
// the pool so their profile can never land on an AI seat.
export function seedMemberNames(configs, members, teamCount, userOwner, selfDisplayName = null) {
  if (!Array.isArray(members) || members.length === 0) return configs;
  const seats = opponentSeats(teamCount, userOwner);
  const self = (selfDisplayName || "").trim();

  // Evict the user's own manager from any opponent seat BEFORE claiming names.
  //
  // The taken-set below blocks a NEW assignment of the user's name but is
  // powerless against one already sitting there — which is exactly the state a
  // re-import lands in: mocks run before importedByUserId existed seeded the
  // importer onto an AI seat, that customName persisted, and now the user
  // seats as themselves while an opponent wears the same name.
  //
  // Edge, and it is the right call: a user who deliberately TYPED their own
  // manager name onto an AI seat loses it too. Typed names outrank imported
  // ones everywhere else, but they can't outrank identity — the user is that
  // manager now, and two seats with one name is never what they meant.
  //
  // Freeing the seat here rather than in a separate effect is what lets the
  // refill below happen in the same pass, so one render settles it.
  const impostors = self ? seats.filter(n => configs[n]?.customName?.trim() === self) : [];
  const base = impostors.length ? { ...configs } : configs;
  impostors.forEach(n => { base[n] = { ...base[n], customName: "" }; });

  // Names already on a seat stay claimed. Without this, a re-seed after
  // userOwner changes could hand the same manager to two seats.
  const taken = new Set(seats.map(n => base[n]?.customName?.trim()).filter(Boolean));
  if (self) taken.add(self);
  const pool = members
    .map(m => (m?.displayName || "").trim())
    .filter(n => n && !taken.has(n));
  // Every early exit returns `base`, not `configs`: an eviction with no
  // replacement available still has to stick. A seat back on its default
  // TEAM_NAME is correct; a second DrewPatrick is not.
  if (!pool.length) return base;

  const open = seats.filter(n => !base[n]?.customName?.trim());
  if (!open.length) return base;

  // Shuffle for variety. Seeded once and then persisted, so the assignment is
  // stable across reloads rather than reshuffling under the user every visit.
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const next = { ...base };
  open.forEach((seat, i) => {
    if (i >= shuffled.length) return;   // members < seats: leave the default
    next[seat] = { ...next[seat], customName: shuffled[i] };
  });
  return next;
}

// Overlay fitted manager profiles onto opponent seats.
//
// DERIVED, never persisted. mockTeamConfigs keeps the user's own (random or
// hand-set) configs untouched, and this returns a copy for the draft builder
// to consume. That's what makes calibrate a true toggle: switch it off and the
// underlying configs are still there, unmodified, so random behavior returns
// without anything to undo.
//
// The seat -> manager join is by customName, which seedMemberNames set to the
// manager's displayName. That's the "profile follows the name" contract: a
// seat shows DrewPatrick because DrewPatrick was assigned to it, so
// DrewPatrick's fitted profile is the one that belongs there.
//
// Only fitted:true profiles are applied. An unfitted manager keeps whatever
// the seat already had, which is the random config — the same thing an
// uncalibrated league gets.
export function applyMemberProfiles(configs, profiles, teamCount, userOwner) {
  if (!Array.isArray(profiles) || !profiles.length) return configs;
  const fitted = new Map(
    profiles.filter(p => p?.fitted && p.displayName).map(p => [p.displayName, p])
  );
  if (!fitted.size) return configs;

  const seats = opponentSeats(teamCount, userOwner);
  let applied = 0;
  const next = { ...configs };
  for (const seat of seats) {
    const p = fitted.get(configs[seat]?.customName?.trim());
    if (!p) continue;
    next[seat] = {
      ...next[seat],
      archetype: p.archetype,
      modifiers: [...p.modifiers],
      // Tells buildDraftTeams the fit owns this seat: no random strategy
      // rolls, no random personalities, no Patient Spender guarantee. Set
      // ONLY here, so every other caller (batch sim, deep sim probe,
      // uncalibrated leagues) takes the original random path.
      fittedProfile: true,
      // null means "use the archetype's own overpay" — the sentinel the
      // config shape already uses, so an unmeasurable manager isn't forced
      // to a number.
      overpay: p.overpay,
    };
    applied++;
  }
  return applied ? next : configs;
}

// How many opponent seats a profile overlay actually reached. Same join as
// applyMemberProfiles, kept beside it so the count on screen can't disagree
// with what the draft builds.
export function countModeledSeats(configs, profiles, teamCount, userOwner) {
  if (!Array.isArray(profiles) || !profiles.length) return 0;
  const fitted = new Set(profiles.filter(p => p?.fitted && p.displayName).map(p => p.displayName));
  return opponentSeats(teamCount, userOwner)
    .filter(n => fitted.has(configs[n]?.customName?.trim()))
    .length;
}

// Read the configs for one league. Falls back to the legacy global key the
// first time a league is opened (copy-forward seed), then to defaults.
function loadTeamConfigs(league) {
  try {
    const own = JSON.parse(localStorage.getItem(cfgKey(league)));
    if (own && typeof own === "object") return sanitizeTeamConfigs(own);
  } catch (e) { /* ignore */ }
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_CONFIGS_KEY));
    if (legacy && typeof legacy === "object") return sanitizeTeamConfigs(legacy);
  } catch (e) { /* ignore */ }
  return sanitizeTeamConfigs(null);
}

// ── Per-league watchlist ────────────────────────────────────────────────────
// Starred players are league-specific ("who I'm targeting in THIS league"), so
// the mock watchlist follows the same per-league pattern as opponent configs:
// per-league key, with the pre-league global key read once as a copy-forward
// seed. The legacy key is never written to and never deleted.
const LEGACY_WATCHLIST_KEY = "auctionArchitect_watchlist";
const wlKey = lg => "auctionArchitect_watchlist_" + (lg || "default");

function loadWatchlist(league) {
  try {
    const own = JSON.parse(localStorage.getItem(wlKey(league)));
    if (Array.isArray(own)) return new Set(own);
  } catch (e) { /* ignore */ }
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_WATCHLIST_KEY));
    if (Array.isArray(legacy)) return new Set(legacy);
  } catch (e) { /* ignore */ }
  return new Set();
}

// ── Per-league avoids (do-not-draft list) ───────────────────────────────────
// Same per-league shape as the watchlist, minus the seed: avoids are a NEW
// feature, so there is no pre-league global key to copy forward — every league
// starts empty. A player can be starred AND avoided at once (no mutual
// exclusion); the two lists are independent Sets.
//
// DISPLAY-ONLY: nothing in the engine reads this. buildDraftTeams and
// calcAiMaxBidV2 are untouched — avoiding a player dims him in the UI, it does
// not change AI behavior or block the user from bidding.
const avKey = lg => "auctionArchitect_myAvoids_" + (lg || "default");

function loadMyAvoids(league) {
  try {
    const own = JSON.parse(localStorage.getItem(avKey(league)));
    if (Array.isArray(own)) return new Set(own);
  } catch (e) { /* ignore */ }
  return new Set();
}

// settingsOpen is the parent's showSettings — an overlay flag passed in the same
// way as tutorialActive, because the Settings modal must idle the auction loop
// (see loopIdle below). Trailing default so the existing arg order is untouched.
export default function useMockDraft(settings, appMode, setAppMode, dynamicPrices, userTierOverrides, roster, tutorialActive, activeLeagueName = null, activeLeague = null, settingsOpen = false) {
  const leagueMembers = activeLeague?.members || null;

  // True roster size from league settings (falls back to the legacy constant).
  // Fixes the hard-stop-at-16 bug so leagues >16 slots (e.g. FFPC=20) fill fully.
  const rosterSize = (settings && settings.positions ? buildRosterSlots(settings.positions).length : 0) || MOCK_ROSTER_SIZE;

  // ── State ─────────────────────────────────────────────────────────────────
  const [mockPhase, setMockPhase] = useState("setup");
  const [mockUserOwner, setMockUserOwner] = useState(MOCK_TEAM_NAMES[0]);
  const [mockTeamConfigs, setMockTeamConfigs] = useState(() => loadTeamConfigs(activeLeagueName));
  const [mockUserSlot, setMockUserSlot] = useState(0);

  // ── Calibrated opponents ──────────────────────────────────────────────────
  // Fitted manager profiles, only when the league opted in. Gated on
  // calibrate so an uncalibrated league does no fitting work at all and
  // behaves exactly as it did before any of this existed.
  // The leaguemate whose account imported this league — the user's own seat.
  // Resolved by user_id, never by name, so a display-name change doesn't
  // silently reassign identity. Null when the league was reached without a
  // Find Leagues lookup, which is today's behavior everywhere.
  const selfMember = useMemo(() => {
    const id = activeLeague?.importedByUserId;
    if (!id || !activeLeague?.members?.length) return null;
    return activeLeague.members.find(m => m?.userId === id) || null;
  }, [activeLeague]);

  const memberProfiles = useMemo(() => {
    if (activeLeague?.calibrate !== true) return null;
    if (!activeLeague.members?.length || !activeLeague.auctionHistory?.length) return null;
    return fitMemberProfiles(activeLeague.auctionHistory, activeLeague.members);
  }, [activeLeague]);

  // What the draft actually builds from. Identical to mockTeamConfigs whenever
  // there's nothing to overlay, so every uncalibrated path is byte-identical.
  const effectiveTeamConfigs = useMemo(
    () => applyMemberProfiles(mockTeamConfigs, memberProfiles, settings.teams, mockUserOwner),
    [mockTeamConfigs, memberProfiles, settings.teams, mockUserOwner]
  );
  const modeledSeatCount = useMemo(
    () => countModeledSeats(mockTeamConfigs, memberProfiles, settings.teams, mockUserOwner),
    [mockTeamConfigs, memberProfiles, settings.teams, mockUserOwner]
  );
  const [mockSpeed, setMockSpeed] = useState("fast");
  const [mockPickNumber, setMockPickNumber] = useState(0);
  const [mockNominatorIndex, setMockNominatorIndex] = useState(0);
  const [mockDraftedPlayers, setMockDraftedPlayers] = useState(new Set());
  const [mockDraftLog, setMockDraftLog] = useState([]);
  const [mockTeams, setMockTeams] = useState({});
  const [mockCurrentPlayer, setMockCurrentPlayer] = useState(null);
  const [mockCurrentBid, setMockCurrentBid] = useState(1);
  const [mockCurrentBidder, setMockCurrentBidder] = useState(null);
  const [mockActiveBidders, setMockActiveBidders] = useState(new Set());
  const [mockSoldPlayer, setMockSoldPlayer] = useState(null);
  // Bumped by the sold-phase recheck so Effect 4 re-enters while an AI bid is
  // still outstanding. Carries no meaning beyond "look again".
  const [bidResolveTick, setBidResolveTick] = useState(0);
  const [mockSearch, setMockSearch] = useState("");
  const [mockPosFilter, setMockPosFilter] = useState(new Set(["ALL"]));
  const [mockMobileTab, setMockMobileTab] = useState("players");
  const [mockShowRecap, setMockShowRecap] = useState(false);
  const [mockPaused, setMockPaused] = useState(false);
  const [mockAutopick, setMockAutopick] = useState(false);
  // Sticky: true once the AI ever drove the user's seat this draft (Auto-Bid /
  // autopick during live play, Simulate-to-End, or Quick Sim). Reset on a fresh
  // draft. Drives whether results show the user's Auto-Bid profile.
  const [userSeatAiDriven, setUserSeatAiDriven] = useState(false);
  const [mockNomTimer, setMockNomTimer] = useState(null);
  const [mockGoingPhase, setMockGoingPhase] = useState(null);
  const [mockUserPassed, setMockUserPassed] = useState(false);
  const [mockNominatingPlayer, setMockNominatingPlayer] = useState(null);
  const [mockOpeningBid, setMockOpeningBid] = useState("1");
  const [mockExpandedTeam, setMockExpandedTeam] = useState(null);
  const [mockWatchlist, setMockWatchlist] = useState(() => loadWatchlist(activeLeagueName));
  const [myAvoids, setMyAvoids] = useState(() => loadMyAvoids(activeLeagueName));
  // How long the USER gets to nominate: "preset" follows the draft-speed setting,
  // "off" means no clock at all, a number is that many seconds. Governs the user's
  // clock only — AI nomination pacing always comes from SPEED_SETTINGS.aiNomSec.
  const [mockNomTimerMode, setMockNomTimerMode] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("auctionArchitect_nomTimerMode"));
      if (saved === "off" || saved === "preset") return saved;
      if (typeof saved === "number" && saved > 0) return saved;
      return "preset";
    } catch { return "preset"; }
  });
  const [mockWatchlistAlert, setMockWatchlistAlert] = useState(false);
  const [mockNominateOverlay, setMockNominateOverlay] = useState(false);
  const [mockNomGlow, setMockNomGlow] = useState(false);
  const [heartbeatPulse, setHeartbeatPulse] = useState(false);
  const [showMockOverflow, setShowMockOverflow] = useState(false);
  const [mockSpeedToast, setMockSpeedToast] = useState(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const mockSpeedToastRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const mockBidTimerRef = useRef(null);
  // True from the moment an AI bid is scheduled until that timer fires or is
  // cleared. Enforces the auction invariant: a nomination may not resolve while
  // an AI bid is scheduled and unfired. Mirrors snipePendingRef, which already
  // guards the same class of race for last-second snipes.
  const bidPendingRef = useRef(false);
  const mockUserPassedRef = useRef(false);
  const mockNomTimerRef = useRef(null);
  const mockSoldTimerRef = useRef(null);
  const mockTickRef = useRef(null);
  const mockTierAnchorsRef = useRef({});
  const mockInflationRef = useRef(0);
  const skipModeRef = useRef(false);
  const snipeCountRef = useRef(0);
  const snipePendingRef = useRef(false);
  const snipesThisNomRef = useRef(0);
  // Consecutive nominator skips with no nomination in between, and the per-draft
  // nomination_fallback emission count. Both reset at draft start; the streak
  // also resets on every successful nomination (in mockNominatePlayer).
  const nomSkipStreakRef = useRef(0);
  const nomFallbackCountRef = useRef(0);
  const bidCountRef = useRef(0);
  const teamsResetFirstRunRef = useRef(true);
  // Which league the in-memory mockTeamConfigs belong to. The persist effect
  // writes to THIS league's key, not activeLeagueName's — on a league switch the
  // state update and the name change land in the same commit, and writing to the
  // new key before the load lands would copy the OLD league's configs into it.
  const configsLeagueRef = useRef(activeLeagueName);
  // Same idea for the watchlist: the persist effect writes to the league the
  // in-memory Set belongs to, not to whatever activeLeagueName currently is.
  const watchlistLeagueRef = useRef(activeLeagueName);
  // Ditto for the avoids list.
  const avoidsLeagueRef = useRef(activeLeagueName);
  // Raised for the one commit in which a league switch is being applied, so the
  // team-count-reset effect doesn't rebuild the outgoing league's configs on top
  // of the incoming league's freshly loaded ones.
  const leagueChangedRef = useRef(false);
  // Framework-agnostic draft-log recorder (src/utils/draftLogger.js). Populated
  // on draft start, holds the JSON-safe event log. Fire-and-forget — logging
  // must never block the auction loop, so all calls use optional chaining and
  // the recorder methods are synchronous, non-throwing pushes.
  const draftLogRef = useRef(null);
  // Persistence guards: once a terminal record (completed / simulated_to_end)
  // has been POSTed for the current draft, the leave-the-page beacon must NOT
  // also send an "abandoned" record. abandonBeaconTsRef debounces the beacon.
  const terminalLogSentRef = useRef(false);
  const abandonBeaconTsRef = useRef(0);

  const [mockMuted, setMockMuted] = useState(() => localStorage.getItem("auctionArchitect_muted") === "true");
  const mockMutedRef = useRef(mockMuted);

  const [mockLeaveTarget, setMockLeaveTarget] = useState(null);
  const [mockExportText, setMockExportText] = useState(null);
  const [mockSavedDraft, setMockSavedDraft] = useState(() => {
    try {
      const saved = localStorage.getItem("auctionArchitect_draftState");
      const parsed = saved ? JSON.parse(saved) : null;
      // A draft started under a dev dial cell and resumed without the URL param
      // would silently continue on STOCK engine knobs — a half-and-half draft
      // that looks legitimate in the record. Refusing to resume would be worse
      // (it would bin real work), so make the mismatch impossible to miss.
      //
      // The NODE_ENV test is written inline rather than relying on
      // activeDialsTag() returning null, because the minifier inlines that to
      // `null` and then CANNOT prove the comparison false — the branch and its
      // message string survived into the production bundle until this guard was
      // added. Verified by grepping the built bundle.
      if (process.env.NODE_ENV !== "production" &&
          parsed && (parsed.dials ?? null) !== activeDialsTag()) {
        console.error(
          "[devDials] saved draft was started under a DIFFERENT dial configuration " +
          "than this session. Resuming it mixes two configurations — start a fresh " +
          `draft instead.  saved: ${parsed.dials ?? "(stock)"}  ·  ` +
          `current: ${activeDialsTag() ?? "(stock)"}`
        );
      }
      return parsed;
    } catch { return null; }
  });
  const [mockEnforcerCaught, setMockEnforcerCaught] = useState(false);
  const [skipMode, setSkipMode] = useState(false);

  // ── Persistence effects ───────────────────────────────────────────────────
  useEffect(() => {
    mockMutedRef.current = mockMuted;
    SoundEngine.muted = mockMuted;
    localStorage.setItem("auctionArchitect_muted", mockMuted);
  }, [mockMuted]);

  // Swap in the new league's opponent configs when the active league changes.
  // Declared BEFORE the team-count effect on purpose: loading a league usually
  // changes settings.teams in the same commit, and effects run in declaration
  // order, so this one must claim the ref and raise the flag first.
  useEffect(() => {
    if (configsLeagueRef.current === activeLeagueName) return;
    // Point the ref at the new league FIRST — the persist effect keys off it, so
    // the configs we're about to set must already be attributed to this league.
    configsLeagueRef.current = activeLeagueName;
    leagueChangedRef.current = true;
    setMockTeamConfigs(loadTeamConfigs(activeLeagueName));
  }, [activeLeagueName]);

  // Name the user's own seat after the manager they are. mockUserOwner is the
  // seat name everywhere (the team-name input, the card label, and
  // simulation.js's displayName for the user slot) — the user's config lives
  // under "__user" and its customName is never rendered, so this is the field
  // that has to move.
  //
  // Only replaces a still-default name, never one the user typed. Declared
  // before the opponent seeding effect so that pass already sees the final
  // owner and derives the right opponent seats.
  useEffect(() => {
    const name = selfMember?.displayName?.trim();
    if (!name) return;
    setMockUserOwner(prev => (TEAM_NAMES.includes(prev) && prev !== name ? name : prev));
  }, [selfMember]);

  // Seat the opponents with this league's real members. Separate from the load
  // effect above so it also covers first mount, where the lazy initializer
  // loaded configs without ever running the load effect.
  //
  // Names only — archetypes, modifiers and every behavioral field are
  // untouched, so a league's opponents play identically named or not. Runs for
  // any league with members, calibrated or not. seedMemberNames returns the
  // same object when there's nothing to fill, so this settles after one pass
  // instead of looping through the persist effect.
  useEffect(() => {
    if (!leagueMembers || leagueMembers.length === 0) return;
    setMockTeamConfigs(prev => seedMemberNames(prev, leagueMembers, settings.teams, mockUserOwner, selfMember?.displayName || null));
  }, [leagueMembers, settings.teams, mockUserOwner, selfMember]);

  // Reset mock setup when team count changes
  useEffect(() => {
    // Skip the initial mount: the lazy initializer already loaded saved
    // team configs from localStorage, and firing here would clobber them.
    // Only rebuild configs on an actual team-count change.
    if (teamsResetFirstRunRef.current) {
      teamsResetFirstRunRef.current = false;
      return;
    }
    if (mockUserSlot >= settings.teams) setMockUserSlot(0);
    // A league switch changes settings.teams too, and the effect above has
    // already loaded the NEW league's configs. Rebuilding here would run against
    // the OLD league's configs and overwrite that load, so skip the rebuild for
    // this cycle — the slot/owner clamps above and below still apply.
    if (!leagueChangedRef.current) {
      // Keep existing entries (don't clobber user customizations on team-count
      // change); only add defaults for names that don't have a config yet.
      setMockTeamConfigs(prev => {
        const configs = {};
        // Preserve reserved "__" keys (e.g. "__user", the user's Auto-Bid profile) —
        // they aren't in TEAM_NAMES and would otherwise be wiped on a team-count change.
        Object.keys(prev).forEach(k => { if (k.startsWith("__")) configs[k] = prev[k]; });
        TEAM_NAMES.slice(0, settings.teams).forEach((name, i) => {
          configs[name] = prev[name] || { customName: "", archetype: DEFAULT_AI_ARCHETYPES[i] || "Balanced", modifiers: [], favoriteTeam: null, overpay: null };
        });
        return configs;
      });
    }
    // A member-derived owner name is legitimate even though it isn't a
    // TEAM_NAME; without this the import would name the user's seat and the
    // team-count change firing in the same commit would immediately undo it.
    const ownerIsKnown = TEAM_NAMES.slice(0, settings.teams).includes(mockUserOwner) ||
      (leagueMembers || []).some(m => m?.displayName === mockUserOwner);
    if (!ownerIsKnown) setMockUserOwner(TEAM_NAMES[0]);
  }, [settings.teams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lower the league-change flag. Declared AFTER the team-count effect so it
  // runs last in the shared commit, which also clears the flag on a league
  // switch that left settings.teams untouched (team-count effect never fired).
  useEffect(() => { leagueChangedRef.current = false; }, [activeLeagueName]);

  useEffect(() => {
    localStorage.setItem(cfgKey(configsLeagueRef.current), JSON.stringify(mockTeamConfigs));
  }, [mockTeamConfigs]);

  // Swap in the new league's watchlist when the active league changes. No
  // team-count interaction here (it's just a Set of names), so unlike the
  // opponent configs this needs no leagueChangedRef guard.
  useEffect(() => {
    if (watchlistLeagueRef.current === activeLeagueName) return;
    // Claim the ref FIRST — the persist effect keys off it, so the Set we're
    // about to set must already be attributed to the incoming league.
    watchlistLeagueRef.current = activeLeagueName;
    setMockWatchlist(loadWatchlist(activeLeagueName));
  }, [activeLeagueName]);

  // Watchlist persists across drafts and page reloads (resetMockDraft deliberately
  // keeps it). It's a Set, so serialize as an array.
  useEffect(() => {
    localStorage.setItem(wlKey(watchlistLeagueRef.current), JSON.stringify([...mockWatchlist]));
  }, [mockWatchlist]);

  // Avoids follow the watchlist exactly: claim the ref, then load the incoming
  // league's list; the persist effect writes to the ref's league.
  useEffect(() => {
    if (avoidsLeagueRef.current === activeLeagueName) return;
    avoidsLeagueRef.current = activeLeagueName;
    setMyAvoids(loadMyAvoids(activeLeagueName));
  }, [activeLeagueName]);

  useEffect(() => {
    localStorage.setItem(avKey(avoidsLeagueRef.current), JSON.stringify([...myAvoids]));
  }, [myAvoids]);

  // Nomination-clock preference persists across drafts and reloads, same as the
  // watchlist — it's a user preference, not draft state.
  useEffect(() => {
    localStorage.setItem("auctionArchitect_nomTimerMode", JSON.stringify(mockNomTimerMode));
  }, [mockNomTimerMode]);

  // Resolve mockNomTimerMode to a countdown length. null = no clock: never start
  // the countdown, never auto-nominate — the user picks at their leisure.
  const getNomSeconds = useCallback(() => {
    if (mockNomTimerMode === "off") return null;
    if (typeof mockNomTimerMode === "number") return mockNomTimerMode;
    return SPEED_SETTINGS[mockSpeed]?.userNomSec || 30;
  }, [mockNomTimerMode, mockSpeed]);

  // Auto-save draft state after each completed pick
  useEffect(() => {
    if (appMode !== "mock" || mockPhase !== "nominating" || mockPickNumber < 2) return;
    // Each team's tierSheet is a Map and would JSON-serialize to {}, so store it
    // as entries. Tier anchors and the inflation index live in refs (not state)
    // and must be persisted too, or the V2 bid engine resumes blind.
    const serializedTeams = Object.fromEntries(
      Object.entries(mockTeams).map(([k, t]) => [
        k,
        { ...t, tierSheet: t.tierSheet instanceof Map ? [...t.tierSheet.entries()] : t.tierSheet },
      ])
    );
    const state = {
      mockTeams: serializedTeams, mockPickNumber, mockNominatorIndex,
      mockDraftedPlayers: [...mockDraftedPlayers],
      mockDraftLog, mockUserSlot, mockUserOwner,
      mockSpeed, mockAutopick, userSeatAiDriven,
      mockTierAnchors: mockTierAnchorsRef.current,
      mockInflation: mockInflationRef.current,
      // Dev-only dial cell, so a resumed draft can be checked against the
      // configuration it was started under. Null in production and in stock
      // sessions, so a stock record is unchanged.
      dials: activeDialsTag(),
    };
    localStorage.setItem("auctionArchitect_draftState", JSON.stringify(state));
  }, [appMode, mockPhase, mockPickNumber, mockTeams, mockNominatorIndex,
      mockDraftedPlayers, mockDraftLog, mockUserSlot, mockUserOwner,
      mockSpeed, mockAutopick, userSeatAiDriven]);

  // ── Computed ──────────────────────────────────────────────────────────────
  // A draft exists in state — true regardless of which page is on screen. Use
  // this for guards that must survive navigation away from mock mode (e.g. the
  // header league lock: the Almanac doesn't end the draft, it just hides it).
  const mockDraftLive = mockPhase !== "setup" && mockPhase !== "complete";
  // ...and this for the on-screen mock UI, which is intentionally appMode-gated.
  const mockDraftActive = appMode === "mock" && mockDraftLive;

  // Latch the AI-driven flag whenever Auto-Bid/autopick is active during a live
  // draft (this is exactly the autopick bid/nominate paths driving the user's
  // seat). autoFinishMock and Quick Sim set the flag explicitly, since they can
  // flip to phase "complete" in the same batch that turns autopick on.
  useEffect(() => {
    if (mockAutopick && mockDraftActive) setUserSeatAiDriven(true);
  }, [mockAutopick, mockDraftActive]);
  const hasUnsavedWork = (Object.keys(roster).length > 0) || mockDraftActive;

  // Warn before closing/refreshing tab if roster has content or draft is active
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const handler = (e) => { e.preventDefault(); };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedWork]);

  // Best-effort persist of an in-progress ("abandoned") draft when the user
  // leaves the page. Registered once. Guards: never send if a terminal record
  // (completed / simulated_to_end) already went out for this draft, never send
  // an already-finalized log, and debounce so pagehide+visibilitychange firing
  // together don't double-post.
  useEffect(() => {
    const flushAbandoned = () => {
      const rec = draftLogRef.current;
      if (!rec) return;
      if (terminalLogSentRef.current) return;      // a terminal record already sent
      if (rec.state.status !== "active") return;   // already finalized
      const now = Date.now();
      if (now - abandonBeaconTsRef.current < 2000) return; // debounce
      abandonBeaconTsRef.current = now;
      try {
        // From the posthog MODULE (App.js imports it as ESM, so window.posthog is undefined).
      const distinctId = posthog?.get_distinct_id?.() ?? null;
        const payload = { ...rec.state, status: "abandoned", distinctId };
        // Beacons post as text/plain; the endpoint JSON.parses string bodies.
        const blob = new Blob([JSON.stringify(payload)], { type: "text/plain" });
        navigator.sendBeacon("/api/draft-log", blob);
      } catch (e) { /* best-effort */ }
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flushAbandoned(); };
    window.addEventListener("pagehide", flushAbandoned);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushAbandoned);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Prevent browser back button from navigating away if there's unsaved work
  useEffect(() => {
    if (!hasUnsavedWork) return;
    window.history.pushState({ stayOnPage: true }, "");
    const handler = () => {
      if (Object.keys(roster).length > 0 || (appMode === "mock" && mockPhase !== "setup" && mockPhase !== "complete")) {
        window.history.pushState({ stayOnPage: true }, "");
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [hasUnsavedWork]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll on the narrow layout during an active draft.
  //
  // ⚠️ 920 MUST MATCH the max-width:920px auction block in styles/auction.css.
  // This is the ONLY gate on `mob-auction-locked` — its CSS (common.css, the
  // position:fixed rule) carries no media query, so if this number drifts above
  // the CSS boundary the body gets position:fixed under the desktop three-panel
  // layout, and if it drifts below, the narrow layout runs with no lock while
  // its panels size themselves with height:calc(100vh - 340px). See the full
  // five-site note on the min-width:920.02px block in auction.css.
  useEffect(() => {
    if (!mockDraftActive) { document.body.classList.remove("mob-auction-locked"); return; }
    const mq = window.matchMedia("(max-width: 920px)");
    const update = () => { if (mq.matches) document.body.classList.add("mob-auction-locked"); else document.body.classList.remove("mob-auction-locked"); };
    update();
    mq.addEventListener("change", update);
    return () => { mq.removeEventListener("change", update); document.body.classList.remove("mob-auction-locked"); };
  }, [mockDraftActive]);

  // The league's rosterable positions and starter requirements, derived once.
  //
  // Keyed on a serialization rather than on settings.positions' identity: a
  // fresh object each render would bust every consumer's memo. Both values are
  // then stable BY VALUE, so the memos and effects below can depend on them
  // directly and can't go stale the way `settings.positions?.K` did — that dep
  // tracked exactly one position while the filter it guarded needed all of them.
  const positionsKey = useMemo(() => JSON.stringify(settings.positions || {}), [settings.positions]);
  const { rosterable, starterMinReqs } = useMemo(() => {
    const positions = JSON.parse(positionsKey);
    return { rosterable: rosterablePositions(positions), starterMinReqs: starterRequirements(positions) };
  }, [positionsKey]);

  const mockAvailablePlayers = useMemo(() => {
    if (appMode !== "mock") return [];
    let list = UNIQUE_PLAYERS.filter(p => !mockDraftedPlayers.has(p.name));
    // Hide every position the league does not roster, not just K. This used to
    // be a K-only filter, so a DEF: 0 league still showed defenses and could
    // force the user to buy one (BN5 Houston Texans $1 under a NO DEF header).
    // rosterablePositions is settings-derived and covers both, plus any future
    // position a format drops.
    list = list.filter(p => rosterable.has(p.pos));
    const userTeam = mockTeams[mockUserSlot];
    if (userTeam && mockPhase === "nominating" && mockNominatorIndex === mockUserSlot) {
      const userEmpty = rosterSize - userTeam.roster.length;
      const userPosCounts = {};
      userTeam.roster.forEach(r => { userPosCounts[r.player.pos] = (userPosCounts[r.player.pos] || 0) + 1; });
      // Starter requirements come from settings, not a literal. The old
      // { QB:1, RB:2, WR:2, TE:1, DEF:1, K:1 } was wrong twice over: DEF/K could
      // never be satisfied in a league that doesn't roster them, so they sat in
      // unfilledPos for the whole draft and fired the lockdown at the end of
      // EVERY draft; and WR: 2 understated a league that starts 3.
      const unfilledPos = [];
      for (const [pos, req] of Object.entries(starterMinReqs)) {
        if ((userPosCounts[pos] || 0) < req) unfilledPos.push(pos);
      }
      if (unfilledPos.length >= userEmpty) {
        const locked = list.filter(p => unfilledPos.includes(p.pos));
        // A must-fill rule must never leave nothing to click. Even with the two
        // fixes above correct, this is the invariant that keeps some future
        // settings combination from soft-locking a user on the nomination clock
        // with an empty panel — the symptom that made this bug user-visible.
        if (locked.length > 0) list = locked;
      }
    }
    list = filterPlayerList(list, mockPosFilter, mockSearch, mockWatchlist);
    // Sort by the same canonical displayed price (${name}-${pos}) so list
    // order matches the $ shown in each row instead of legacy player.value.
    const priceOf = (p) => dynamicPrices?.get(`${p.name}-${p.pos}`) ?? 1;
    list.sort((a, b) => priceOf(b) - priceOf(a));
    return list;
  }, [appMode, mockDraftedPlayers, mockPosFilter, mockSearch, mockWatchlist, mockTeams, mockUserSlot, mockPhase, mockNominatorIndex, rosterable, starterMinReqs, dynamicPrices]);

  // Keeper roster entries, shaped like draft-log picks: [{ player, price }].
  //
  // DERIVED, never stored. A keeper is exactly a roster entry with no sale event
  // behind it — everything else on a roster got there through the draft log. That
  // definition costs no new state and no new persistence, and it survives a
  // resume for free because both inputs are already persisted.
  //
  // It exists because some surfaces answer "is this player still available?" from
  // the DRAFT LOG rather than from the drafted-set, and keepers are in the
  // drafted-set but never in the log. TierViewer is the one that matters: it
  // builds its sold-map from draftPicks, so without this a kept player renders as
  // available at his board price, in the very panel whose job is to tell you who
  // is left. Feeding the keepers in at their KEEPER price makes every consumer
  // correct with no change to the consumer.
  const mockKeeperPicks = useMemo(() => {
    if (!mockDraftLog.length && !Object.keys(mockTeams).length) return [];
    const sold = new Set(mockDraftLog.map(l => l.player.name));
    const out = [];
    for (const t of Object.values(mockTeams)) {
      for (const r of t.roster || []) {
        if (!sold.has(r.player.name)) out.push({ player: r.player, price: r.price });
      }
    }
    return out;
  }, [mockTeams, mockDraftLog]);

  const mockUserTeam = useMemo(() => {
    return mockTeams[mockUserSlot] || null;
  }, [mockTeams, mockUserSlot]);

  const mockIsUserTurn = useMemo(() => {
    return mockPhase === "nominating" && mockNominatorIndex === mockUserSlot;
  }, [mockPhase, mockNominatorIndex, mockUserSlot]);

  const mockUserMaxBid = useMemo(() => {
    if (!mockUserTeam) return 0;
    const emptyMockSlots = rosterSize - mockUserTeam.roster.length;
    if (emptyMockSlots <= 1) return mockUserTeam.budget;
    return mockUserTeam.budget - (emptyMockSlots - 1);
  }, [mockUserTeam, rosterSize]);

  // ── Sound ─────────────────────────────────────────────────────────────────
  const playMockSound = useCallback((type) => {
    if (mockMutedRef.current || skipModeRef.current) return;
    try {
      switch (type) {
        case "bid": SoundEngine.playBid(); break;
        case "goingOnce": SoundEngine.playGoingOnce(); break;
        case "goingTwice": SoundEngine.playGoingTwice(); break;
        case "sold": SoundEngine.playSold(); break;
        case "yourTurn": SoundEngine.playYourTurn(); break;
        case "target": SoundEngine.playTarget(); break;
        default: break;
      }
    } catch (e) { /* ignore audio errors */ }
  }, []);

  // Auto-switch mobile tab when it's the user's turn to nominate
  useEffect(() => {
    if (mockIsUserTurn && !mockAutopick) {
      setMockMobileTab("players");
      setMockNominateOverlay(true);
      playMockSound("yourTurn");
    }
  }, [mockIsUserTurn, mockAutopick, playMockSound]);

  // ── Action callbacks ──────────────────────────────────────────────────────
  // Wall-clock anchor for mock_draft_completed durations. Set on start and on
  // resume, so a resumed draft reports time-in-session, not time-since-save.
  const mockDraftStartedAtRef = useRef(null);

  const startMockDraft = useCallback(() => {
    captureMockDraftStarted({
      teams: settings.teams,
      budget: settings.budget,
      // Literal SFLEX slot count — this dimension's meaning must stay what it
      // has always been. `format` carries the 2QB split.
      isSflex: (settings.positions.SFLEX || 0) > 0,
      format: leagueFormatLabel(settings.positions),
      positions: settings.positions,
      scoring: settings.scoring,
      // Same pure function MockSetup lights the chip from, on the same inputs.
      anchorPreset: marketAnchorPresetFor(settings.marketAnchor, settings.positions, settings.budget),
    });
    mockDraftStartedAtRef.current = Date.now();
    const teams = buildDraftTeams(mockUserOwner, mockUserSlot, effectiveTeamConfigs, settings.teams, settings.budget, userTierOverrides, false, settings);
    // ── Keepers ────────────────────────────────────────────────────────────
    // Seeded here, BEFORE setMockTeams, by the one function that does all four
    // writes together (roster, budget, drafted-set, derived state). See
    // applyKeepers for why splitting them hangs the draft.
    //
    // The whole guard exists for addendum B: with no keepers, nothing here is
    // entered at all, so a zero-keeper league cannot be perturbed by code it
    // never runs. `hasKeepers` is a cheap key scan, not a resolution pass.
    //
    // Everything downstream already reads from the seeded state: the
    // availability filters, `emptySlots` at five sites, and posCount/posSpent
    // inside calcAiMaxBidV2 are all derived from team.roster and the
    // drafted-set. There is nothing else to wire.
    let keptNames = null;
    // For the draft log. Derived from what was ACTUALLY seeded onto rosters, not
    // from what the config asked for, so a refused or unresolvable keeper is not
    // counted as one that happened.
    let keeperCount = 0, keeperSpend = 0;
    if (hasKeepers(effectiveTeamConfigs)) {
      const { byOwner, unresolved, warnings } = resolveKeepers(effectiveTeamConfigs, settings);
      const r = applyKeepers(teams, byOwner, rosterSize);
      keptNames = r.keptNames;
      keeperCount = r.applied;
      keeperSpend = Object.values(teams).reduce(
        (s, t) => s + (t.roster || []).reduce((a, e) => a + e.price, 0), 0);
      // Never silent. A dropped keeper is the one failure the user cannot see
      // for themselves — they would draft against a roster that isn't there.
      if (unresolved.length) console.warn("[keepers] not on this year's board, dropped:", unresolved.map(u => u.name).join(", "));
      if (r.rejected.length) console.warn("[keepers] refused at the engine boundary:", r.rejected);
      warnings.forEach(w => console.warn("[keepers]", w));
    }
    setMockTeams(teams);
    // Start the draft-log recorder. Seats carry the resolved archetype/modifiers/
    // favoriteTeam from buildDraftTeams (which folds in random QB/TE/PS rolls),
    // not the raw configs, so the log reflects the population actually drafting.
    // appVersion segments drafts by engine build: package version plus the
    // Vercel commit SHA when the build exposes one (CRA only inlines
    // REACT_APP_*; VERCEL_GIT_COMMIT_SHA is checked for non-CRA/SSR builds).
    const gitSha = process.env.REACT_APP_GIT_SHA || process.env.VERCEL_GIT_COMMIT_SHA || null;
    const appVersion = gitSha ? `${APP_VERSION}+${gitSha.slice(0, 7)}` : APP_VERSION;
    // How the league was set up — the SHAPE only, never the league name. The
    // server can tell whether settings match the defaults, but it cannot know
    // whether a saved league is loaded, so that half has to come from here.
    const leagueClass = activeLeagueName
      ? "saved"
      : (isDefaultLeague(settings) ? "default" : "edited");
    draftLogRef.current = createDraftLog({
      settings,
      userSeat: mockUserSlot,
      speed: mockSpeed,
      autopick: !!mockAutopick,
      appVersion,
      // Viewport width at draft start. Purely a client fact — it makes the
      // narrow-screen cohort visible per draft instead of only in aggregate.
      viewport: typeof window !== "undefined" ? window.innerWidth : null,
      leagueClass,
      // Keeper drafts are a different pricing regime, so they must be
      // segmentable OUT of the price corpus. 0/0 on every non-keeper draft.
      keeperCount,
      keeperSpend,
      // Label Ben's own test drafts in the Discord notification. internal comes
      // from the analytics gate's persisted flag; devLabel is an optional name.
      internal: isInternalUser(),
      devLabel: (typeof localStorage !== "undefined" && localStorage.getItem("auctionArchitect_devLabel")) || null,
      // Dev-only dial cell, when one is active. Null in production and in every
      // stock session — this marks a feel-pass draft so it can be filtered out.
      dials: activeDialsTag(),
      seats: Object.entries(teams).map(([id, t]) => ({
        seat: Number(id),
        name: t.name,
        archetype: t.profile?.archetype || null,
        modifiers: t.profile?.modifiers || [],
        favoriteTeam: t.profile?.favoriteTeam || null,
        overpay: t.profile?.overpay ?? null,
      })),
    });
    // New draft: reset the persistence guards.
    terminalLogSentRef.current = false;
    abandonBeaconTsRef.current = 0;
    // Draft-start ping: non-blocking POST of the config-only log (events are
    // still empty here) with status "started" — persists the record early and
    // lets the endpoint fire a "draft started" notification. Fire-and-forget.
    try {
      // From the posthog MODULE (App.js imports it as ESM, so window.posthog is undefined).
      const distinctId = posthog?.get_distinct_id?.() ?? null;
      const payload = { ...draftLogRef.current.state, status: "started", distinctId };
      fetch("/api/draft-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    } catch (e) { /* never block the draft on telemetry */ }
    setMockPickNumber(1);
    setMockNominatorIndex(0);
    // Kept players are drafted from pick 1. A keeper occupies a roster slot, so
    // counting it here is what makes the loop's terminator
    // (`totalPicked >= rosterSize * teams`) land after exactly
    // rosterSize*teams - keeperCount live picks.
    setMockDraftedPlayers(keptNames ? new Set(keptNames) : new Set());
    setMockDraftLog([]);
    setMockCurrentPlayer(null);
    setMockCurrentBid(1);
    setMockCurrentBidder(null);
    setMockActiveBidders(new Set());
    setMockSoldPlayer(null);
    setUserSeatAiDriven(false);
    setMockPhase("nominating");
    snipeCountRef.current = 0;
    SoundEngine.startAmbiance();
  }, [mockUserOwner, mockUserSlot, effectiveTeamConfigs, settings.teams, settings.budget, userTierOverrides, mockSpeed, mockAutopick, activeLeagueName, rosterSize]);

  const resetMockDraft = useCallback(() => {
    if (mockBidTimerRef.current) clearTimeout(mockBidTimerRef.current);
    if (mockNomTimerRef.current) clearTimeout(mockNomTimerRef.current);
    if (mockSoldTimerRef.current) clearTimeout(mockSoldTimerRef.current);
    if (mockTickRef.current) { clearInterval(mockTickRef.current); mockTickRef.current = null; }
    mockBidTimerRef.current = null;
    mockNomTimerRef.current = null;
    mockSoldTimerRef.current = null;
    SoundEngine.stopFastForward();
    SoundEngine.stopAmbiance();
    setMockPhase("setup");
    setMockPickNumber(0);
    setMockNominatorIndex(0);
    setMockDraftedPlayers(new Set());
    setMockDraftLog([]);
    setMockTeams({});
    setMockCurrentPlayer(null);
    setMockCurrentBid(1);
    setMockCurrentBidder(null);
    setMockActiveBidders(new Set());
    setMockSoldPlayer(null);
    setMockSearch("");
    setMockPosFilter(new Set(["ALL"]));
    setMockPaused(false);
    setMockAutopick(false);
    setUserSeatAiDriven(false);
    setSkipMode(false);
    skipModeRef.current = false;
    setMockNomTimer(null);
    setMockGoingPhase(null);
    setMockNominatingPlayer(null);
    setMockOpeningBid("1");
    setMockExpandedTeam(null);
    setMockWatchlistAlert(false);
    mockTierAnchorsRef.current = {};
    mockInflationRef.current = 0;
    nomSkipStreakRef.current = 0;
    nomFallbackCountRef.current = 0;
    localStorage.removeItem("auctionArchitect_draftState");
    setMockSavedDraft(null);
    setMockEnforcerCaught(false);
    // Drop the recorder so an explicitly-reset draft is never beaconed as
    // "abandoned" on leave, and clear the terminal-sent guard.
    draftLogRef.current = null;
    terminalLogSentRef.current = false;
    // Note: mockWatchlist and mockTeamConfigs are NOT cleared — persist across drafts
  }, []);

  const resumeMockDraft = useCallback(() => {
    if (!mockSavedDraft) return;
    const s = mockSavedDraft;
    // Rebuild each team's tierSheet Map from the saved entries. Old/corrupt saves
    // (pre-serialization-fix) stored tierSheet as {} — not an array — so regenerate
    // a fresh personal sheet for those teams rather than crashing on first AI bid.
    const rehydratedTeams = Object.fromEntries(
      Object.entries(s.mockTeams || {}).map(([k, t]) => [
        k,
        {
          ...t,
          tierSheet: Array.isArray(t.tierSheet)
            ? new Map(t.tierSheet)
            : generatePersonalTierSheet(PLAYERS, Math.floor(Math.random() * 2147483647)),
        },
      ])
    );
    setMockTeams(rehydratedTeams);
    mockTierAnchorsRef.current = s.mockTierAnchors || {};
    mockInflationRef.current = s.mockInflation || 0;
    setMockPickNumber(s.mockPickNumber);
    setMockNominatorIndex(s.mockNominatorIndex);
    setMockDraftedPlayers(new Set(s.mockDraftedPlayers));
    setMockDraftLog(s.mockDraftLog);
    setMockUserSlot(s.mockUserSlot);
    setMockUserOwner(s.mockUserOwner);
    setMockSpeed(s.mockSpeed);
    setMockAutopick(s.mockAutopick || false);
    setUserSeatAiDriven(s.userSeatAiDriven || false);
    setMockPaused(true);
    setMockCurrentPlayer(null);
    setMockCurrentBid(1);
    setMockCurrentBidder(null);
    setMockActiveBidders(new Set());
    setMockGoingPhase(null);
    setMockPhase("nominating");
    setMockSavedDraft(null);
    mockDraftStartedAtRef.current = Date.now();
  }, [mockSavedDraft]);

  // ── Skip-to-my-pick ────────────────────────────────────────────────────────

  // Determine whether the user still needs starters at a given position
  const getUserMustFillPositions = useCallback(() => {
    const userTeam = mockTeams[mockUserSlot];
    if (!userTeam) return new Set();
    const posCounts = {};
    userTeam.roster.forEach(r => { posCounts[r.player.pos] = (posCounts[r.player.pos] || 0) + 1; });
    const minReqs = { QB: 1, RB: 2, WR: 2, TE: 1, DEF: 1, K: 1 };
    const mustFill = new Set();
    for (const [pos, req] of Object.entries(minReqs)) {
      if ((posCounts[pos] || 0) < req) mustFill.add(pos);
    }
    return mustFill;
  }, [mockTeams, mockUserSlot]);

  const shouldSkipStop = useCallback((nominatedPlayer) => {
    if (!skipModeRef.current) return false;
    if (mockWatchlist.has(nominatedPlayer.name)) return true;
    return false;
  }, [mockWatchlist]);

  const stopSkipMode = useCallback(() => {
    skipModeRef.current = false;
    setSkipMode(false);
    mockUserPassedRef.current = false;
    setMockUserPassed(false);
    setMockPaused(true);
    SoundEngine.muted = mockMutedRef.current; // restore user's mute preference
    SoundEngine.stopFastForward();
    SoundEngine.pauseAmbiance();
  }, []);

  const startSkip = useCallback(() => {
    if (!mockDraftActive) return;
    if (mockPhase === "nominating" && mockNominatorIndex === mockUserSlot && !mockAutopick) return;
    skipModeRef.current = true;
    setSkipMode(true);
    mockUserPassedRef.current = true;
    setMockUserPassed(true);
    if (mockPhase === "bidding") {
      setMockActiveBidders(prev => { const next = new Set(prev); next.delete(mockUserSlot); return next; });
    }
    setMockPaused(false);
    SoundEngine.muted = true; // silence everything during skip
    SoundEngine.stopFastForward();
    SoundEngine.stopAmbiance();
  }, [mockDraftActive, mockPhase, mockNominatorIndex, mockUserSlot, mockAutopick]);

  const clearHandoffPause = useCallback(() => {
    setMockPaused(p => p ? false : p);
  }, []);

  // Auto-pause a live draft when the page goes hidden (tab switch, app switch,
  // phone lock). Without this the auction loop keeps running in the background
  // and the user comes back to players already sold.
  //
  // Deliberately separate from the abandoned-log flush effect (pagehide +
  // debounce) — that one is load-bearing and must not grow responsibilities.
  // Lives down here rather than beside it because it depends on stopSkipMode.
  //
  // PAUSE ONLY, NEVER AUTO-RESUME. Returning to the tab leaves the draft paused
  // until the user clicks Resume: resuming for them would start bidding before
  // they've re-oriented and would race the timer effects back to life. It also
  // means this can never clobber a pause the user set themselves.
  //
  // visibilitychange only, not blur — blur fires on desktop for things like
  // clicking the bookmarks bar or a devtools panel, which shouldn't pause a draft.
  useEffect(() => {
    if (!mockDraftLive) return;
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      // Skip mode gets the full interrupt, not a bare pause: stopSkipMode is the
      // existing tested freeze path (it pauses AND clears skipModeRef /
      // mockUserPassed and restores the user's mute preference). A bare pause
      // would leave a half-state — paused but still force-muted with
      // mockUserPassedRef set — so Resume would silently blast through picks
      // for someone who has just come back. Re-triggering skip is one tap.
      if (skipModeRef.current) { stopSkipMode(); return; }
      setMockPaused(true);
      // Same sound teardown as the manual pause in toggleMockPaused — otherwise
      // the ambiance loop keeps playing over whatever app the user switched to.
      // Resume already calls resumeAmbiance(), so this stays symmetric.
      SoundEngine.pauseAmbiance();
      SoundEngine.stopFastForward();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [mockDraftLive, stopSkipMode]);

  // Finalize the recorder with a terminal status and persist it to Upstash.
  // Marks the terminal-sent guard so the leave-page beacon can't follow a
  // finished draft with an "abandoned" record. keepalive lets the POST survive
  // the navigation that often accompanies draft completion. Fire-and-forget.
  const finalizeAndPersistLog = useCallback((status) => {
    const rec = draftLogRef.current;
    if (!rec) return;
    rec.finalize(status);
    terminalLogSentRef.current = true;
    try {
      // From the posthog MODULE (App.js imports it as ESM, so window.posthog is undefined).
      const distinctId = posthog?.get_distinct_id?.() ?? null;
      const payload = { ...rec.state, distinctId };
      fetch("/api/draft-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* best-effort */ });
    } catch (e) { /* never block the draft on telemetry */ }
  }, []);

  const mockNominatePlayer = useCallback((player, openingBid = 1) => {
    if (mockPhase !== "nominating") return;
    // Every successful nomination flows through here, so this is the one place
    // the skip streak needs clearing — the streak only ever means "consecutive
    // skips since the last nomination".
    nomSkipStreakRef.current = 0;
    clearHandoffPause();
    setMockNominateOverlay(false);
    const nominatorTeam = mockTeams[mockNominatorIndex];
    const nominatorEmpty = nominatorTeam ? rosterSize - nominatorTeam.roster.length : 1;
    const nominatorMax = nominatorTeam ? (nominatorEmpty <= 1 ? nominatorTeam.budget : nominatorTeam.budget - (nominatorEmpty - 1)) : 1;
    let bid = Math.max(1, Math.min(openingBid, nominatorMax));
    if (nominatorTeam?.profile) {
      const np = nominatorTeam.profile;
      const nPos = player.pos;
      if (np.starsMode) {
        const sc = nominatorTeam.roster.filter(p => p.price >= 40).length;
        if (sc >= 3) bid = Math.min(bid, 3);
      }
      if (np.rbCap < 999 && nPos === "RB") bid = Math.min(bid, np.rbCap);
      if (np.heroRB && nPos === "RB" && nominatorTeam.heroRBDrafted) bid = Math.min(bid, 5);
      if (np.rbSpendCap > 0 && nPos === "RB") {
        const rbS = nominatorTeam.roster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
        bid = Math.min(bid, Math.max(1, np.rbSpendCap - rbS));
      }
      if (np.archetype === "Balanced") bid = Math.min(bid, 40);
    }
    setMockCurrentPlayer(player);
    setMockCurrentBid(bid);
    setMockCurrentBidder(mockNominatorIndex);
    draftLogRef.current?.nominate({ pick: mockPickNumber, nominatorSeat: mockNominatorIndex, player, openingBid: bid });
    const bidders = new Set();
    Object.entries(mockTeams).forEach(([id, team]) => {
      const i = parseInt(id);
      const emptySlots = rosterSize - team.roster.length;
      if (emptySlots <= 0 || team.budget < bid) return;
      if (i !== mockUserSlot || mockAutopick || skipModeRef.current) {
        const pCount = team.roster.filter(p => p.player.pos === player.pos).length;
        if (player.pos === "K" && pCount >= 1) return;
        if (player.pos === "DEF" && pCount >= (team.maxDef || 1)) return;
        if (player.pos === "QB" && pCount >= (team.maxQB || 2)) return;
        if (player.pos === "TE" && pCount >= (team.maxTE || 2)) return;
      }
      if (i === mockUserSlot && skipModeRef.current) return; // user auto-passes during skip
      bidders.add(i);
    });
    setMockActiveBidders(bidders);
    setMockNominatingPlayer(null);
    setMockOpeningBid("1");
    setMockGoingPhase(null);
    setMockNomTimer(null);
    bidCountRef.current = 1; // opening bid counts
    mockUserPassedRef.current = false;
    setMockUserPassed(false);
    setMockPhase("bidding");
    setMockEnforcerCaught(Math.random() < 0.18);
    if (mockNominatorIndex !== mockUserSlot) setMockMobileTab("auction");
    const isWatchlistHit = mockWatchlist.has(player.name);
    if (isWatchlistHit) {
      setMockWatchlistAlert(true);
      playMockSound("target");
    }
    // Skip-to-my-pick: stop if watchlist player nominated, auto-bid minimum
    if (skipModeRef.current && isWatchlistHit) {
      stopSkipMode();
      // Re-add user to active bidders and place opening bid
      setMockActiveBidders(prev => { const next = new Set(prev); next.add(mockUserSlot); return next; });
      const autoBid = bid + 1;
      const userTeam = mockTeams[mockUserSlot];
      const userEmpty = userTeam ? rosterSize - userTeam.roster.length : 1;
      const userMax = userTeam ? (userEmpty <= 1 ? userTeam.budget : userTeam.budget - (userEmpty - 1)) : 1;
      if (autoBid <= userMax) {
        setMockCurrentBid(autoBid);
        setMockCurrentBidder(mockUserSlot);
        setMockGoingPhase(null);
      }
    } else if (!skipModeRef.current) {
      SoundEngine.stopFastForward();
    }
  }, [mockPhase, mockNominatorIndex, mockTeams, mockWatchlist, playMockSound, mockUserSlot, mockAutopick, stopSkipMode]);

  const processSoldPlayer = useCallback((player, price, winnerId) => {
    SoundEngine.stopFastForward();
    SoundEngine.rotateAmbiance();
    SoundEngine.setAmbianceIntensity(0);
    setMockTeams(prev => {
      const updated = { ...prev };
      const team = { ...updated[winnerId] };
      team.roster = [...team.roster, { player, price }];
      team.budget = team.budget - price;
      if (team.budget < 0) {
        console.error(`[BUG] Team ${team.name} went negative: $${team.budget} after buying ${player.name} for $${price}`);
        team.budget = 0;
      }
      if (team.profile?.starsMode) {
        const newStarCount = team.roster.filter(p => p.price >= 40).length;
        if (price >= 40) {
          team.starsPurchased = (team.starsPurchased || 0) + 1;
          const sp = { ...(team.starPositions || {}) };
          sp[player.pos] = (sp[player.pos] || 0) + 1;
          team.starPositions = sp;
        }
      }
      if (team.profile?.heroRB && player.pos === "RB" && player.value >= 40 && !team.heroRBDrafted) {
        team.heroRBDrafted = true;
      }
      if (team.homerReserve > 0 && team.profile?.favoriteTeam && player.team === team.profile.favoriteTeam) {
        team.homerReserve = Math.max(0, team.homerReserve - price);
      }
      updated[winnerId] = team;
      return updated;
    });
    // The tally is utils/roomTemp.js's, shared with simulateDraftSync and the
    // Live Board so the three surfaces cannot report different rooms. UNITS
    // (floor/ceiling are budget FRACTIONS) documented on tierMidDollars.
    const anchorTierObj = tierForPlayer(player);
    if (anchorTierObj) {
      const tier = anchorTierObj.tier;
      const tierMid = tierMidDollars(player, settings, dynamicPrices);
      if (price >= tierMid * 0.5) {
        if (!mockTierAnchorsRef.current[player.pos]) mockTierAnchorsRef.current[player.pos] = {};
        if (mockTierAnchorsRef.current[player.pos][tier] === undefined) {
          mockTierAnchorsRef.current[player.pos][tier] = price;
        }
      }
      mockInflationRef.current += (price - tierMid);
    }
    // Single sale-resolution point — captures every pick (live + AI) with one hook.
    draftLogRef.current?.sale({ pick: mockPickNumber, player, winnerSeat: winnerId, price });
    setMockDraftedPlayers(prev => {
      const next = new Set(prev);
      next.add(player.name);
      return next;
    });
    setMockDraftLog(prev => [{
      pickNumber: mockPickNumber,
      player,
      price,
      winnerId,
      winnerName: mockTeams[winnerId]?.name || `Team ${winnerId + 1}`,
      nominatorId: mockNominatorIndex,
      nominatorName: mockTeams[mockNominatorIndex]?.name || `Team ${mockNominatorIndex + 1}`,
      bidCount: bidCountRef.current,
    }, ...prev]);
    setMockSoldPlayer({ player, price, winnerId, winnerName: mockTeams[winnerId]?.name });
    if (winnerId === mockUserSlot) {
      const colors = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];
      confetti({ particleCount: 60, angle: 60, spread: 70, origin: { x: 0, y: 0.6 }, colors, gravity: 1.4, scalar: 0.9, ticks: 120 });
      confetti({ particleCount: 60, angle: 120, spread: 70, origin: { x: 1, y: 0.6 }, colors, gravity: 1.4, scalar: 0.9, ticks: 120 });
      SoundEngine.playWin();
    }
    setMockCurrentPlayer(null);
    setMockCurrentBid(1);
    setMockCurrentBidder(null);
    setMockActiveBidders(new Set());
    setMockGoingPhase(null);
    snipesThisNomRef.current = 0;
    snipePendingRef.current = false;
    setMockWatchlistAlert(false);
    const totalPicked = mockDraftedPlayers.size + 1;
    if (totalPicked >= rosterSize * settings.teams) {
      if (mockSoldTimerRef.current) clearTimeout(mockSoldTimerRef.current);
      const completeMs = skipModeRef.current ? 0 : 1500;
      mockSoldTimerRef.current = setTimeout(() => {
        setMockSoldPlayer(null);
        SoundEngine.stopAmbiance();
        SoundEngine.stopFastForward();
        setMockPhase("complete");
        finalizeAndPersistLog("completed");
        localStorage.removeItem("auctionArchitect_draftState");
        captureMockDraftCompleted({
          picks: totalPicked,
          viaSimulate: false,
          durationSec: mockDraftStartedAtRef.current ? Math.round((Date.now() - mockDraftStartedAtRef.current) / 1000) : null,
        });
      }, completeMs);
      return;
    }
    const advanceToNextNom = () => {
      setMockSoldPlayer(null);
      setMockTeams(currentTeams => {
        let next = (mockNominatorIndex + 1) % settings.teams;
        let safety = 0;
        while (safety < settings.teams) {
          const nextTeam = currentTeams[next];
          if (nextTeam && nextTeam.roster.length < rosterSize) break;
          next = (next + 1) % settings.teams;
          safety++;
        }
        setMockNominatorIndex(next);
        return currentTeams;
      });
      setMockPickNumber(prev => prev + 1);
      setMockPhase("nominating");
    };

    const soldDelay = SPEED_SETTINGS[mockSpeed]?.soldDelayMs || 1000;
    const finalSoldDelay = skipModeRef.current ? 0 : mockUserPassedRef.current ? Math.max(soldDelay * 0.3, 300) : soldDelay;
    if (mockSoldTimerRef.current) clearTimeout(mockSoldTimerRef.current);
    mockSoldTimerRef.current = setTimeout(advanceToNextNom, finalSoldDelay);
    // The two calibration-rider reads above (the tierMid units fix, e1e737d)
    // are the reason the last two deps are here: without them a calibrate
    // toggle or a budget change leaves this callback closed over the old
    // values until something else re-creates it. Identical in-draft — neither
    // changes mid-auction — so this only closes the stale-closure window.
  }, [mockPickNumber, mockTeams, mockNominatorIndex, mockDraftedPlayers, mockSpeed, mockUserSlot, settings.teams,
      dynamicPrices?.__leagueMultipliers, settings?.budget]);

  const mockPlaceBid = useCallback((increment = 1) => {
    if (mockPhase !== "bidding" || !mockCurrentPlayer) return;
    const newBid = mockCurrentBid + increment;
    if (newBid > mockUserMaxBid) return;
    clearHandoffPause();
    bidCountRef.current++;
    draftLogRef.current?.bid({ pick: mockPickNumber, seat: mockUserSlot, isUser: true, amount: newBid });
    setMockCurrentBid(newBid);
    setMockCurrentBidder(mockUserSlot);
    setMockGoingPhase(null);
    if (mockSpeed !== "instant") playMockSound("bid");
  }, [mockPhase, mockCurrentPlayer, mockCurrentBid, mockUserMaxBid, mockUserSlot, mockSpeed, playMockSound]);

  const mockPassBid = useCallback(() => {
    if (mockPhase !== "bidding") return;
    clearHandoffPause();
    setMockActiveBidders(prev => {
      const next = new Set(prev);
      next.delete(mockUserSlot);
      return next;
    });
    mockUserPassedRef.current = true;
    setMockUserPassed(true);
    SoundEngine.startFastForward();
  }, [mockPhase, mockUserSlot]);

  const toggleMockPaused = useCallback(() => {
    setMockPaused(p => {
      if (!p) {
        SoundEngine.pauseAmbiance();
        SoundEngine.stopFastForward();
      } else {
        SoundEngine.resumeAmbiance();
        if (mockUserPassedRef.current) SoundEngine.startFastForward();
      }
      return !p;
    });
  }, []);

  const toggleWatchlist = useCallback((playerId) => {
    setMockWatchlist(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }, []);

  // Independent of the watchlist — a player can be both starred and avoided.
  const toggleAvoid = useCallback((playerId) => {
    setMyAvoids(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }, []);

  const autoFinishMock = useCallback(() => {
    if (mockBidTimerRef.current) { clearTimeout(mockBidTimerRef.current); mockBidTimerRef.current = null; }
    if (mockNomTimerRef.current) { clearTimeout(mockNomTimerRef.current); mockNomTimerRef.current = null; }
    if (mockSoldTimerRef.current) { clearTimeout(mockSoldTimerRef.current); mockSoldTimerRef.current = null; }
    if (mockTickRef.current) { clearInterval(mockTickRef.current); mockTickRef.current = null; }
    let simTeams = {};
    for (const [id, t] of Object.entries(mockTeams)) {
      simTeams[id] = { ...t, roster: [...t.roster], starPositions: { ...(t.starPositions || {}) } };
    }
    let simDrafted = new Set(mockDraftedPlayers);
    let simLog = [...mockDraftLog].reverse();
    let simPick = mockPickNumber;
    let simNomIdx = mockNominatorIndex;
    if (mockPhase === "bidding" && mockCurrentPlayer) {
      const eliteCount = simLog.filter(l => l.price >= 50).length;
      const tierAnchors = mockTierAnchorsRef.current || {};
      const inflationVal = mockInflationRef.current || 0;
      const willingBidders = [];
      mockActiveBidders.forEach(id => {
        if (id === mockCurrentBidder) return;
        const team = simTeams[id];
        const emptySlots = rosterSize - team.roster.length;
        const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
        const maxBid = calcAiMaxBidV2(team.profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount, teamCaps, team, simPick, tierAnchors, inflationVal, simTeams, settings.positions.SFLEX || 0, settings, dynamicPrices, simDrafted, simNomIdx, rosterSize);
        if (maxBid > mockCurrentBid) willingBidders.push({ id, maxBid });
      });
      // skipIndex -1 is deliberate: auto-finish means the user delegated the rest of the
      // draft, and simulateDraftSync fills his team below — so his seat is AI-controlled
      // here, unlike the live loop above.
      applyValueRecognitionFloor({
        player: mockCurrentPlayer, currentBid: mockCurrentBid,
        excludeIndex: mockCurrentBidder, skipIndex: -1,
        willingBidders, teams: simTeams, rosterSize, priceMap: dynamicPrices, budget: settings?.budget ?? 200,
        recheckMaxBid: (team, emptySlots, teamCaps) => calcAiMaxBidV2(
          team.profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount,
          teamCaps, team, simPick, tierAnchors, inflationVal, simTeams,
          settings.positions.SFLEX || 0, settings, dynamicPrices, simDrafted, simNomIdx,
          rosterSize),
      });
      let winnerId = mockCurrentBidder;
      let winPrice = mockCurrentBid;
      if (willingBidders.length > 0) {
        let highestMax = 0, highestId = willingBidders[0].id, secondMax = mockCurrentBid;
        willingBidders.forEach(({ id, maxBid }) => {
          if (maxBid > highestMax) { secondMax = Math.max(secondMax, highestMax); highestMax = maxBid; highestId = id; }
          else if (maxBid > secondMax) secondMax = maxBid;
        });
        winnerId = highestId;
        winPrice = Math.min(Math.max(mockCurrentBid, secondMax + 1), highestMax);
      }
      const winTeam = simTeams[winnerId];
      winTeam.roster.push({ player: mockCurrentPlayer, price: winPrice });
      winTeam.budget -= winPrice;
      if (winTeam.profile?.starsMode && winPrice >= 40) {
        winTeam.starsPurchased = (winTeam.starsPurchased || 0) + 1;
        if (!winTeam.starPositions) winTeam.starPositions = {};
        winTeam.starPositions[mockCurrentPlayer.pos] = (winTeam.starPositions[mockCurrentPlayer.pos] || 0) + 1;
      }
      if (winTeam.profile?.heroRB && mockCurrentPlayer.pos === "RB" && mockCurrentPlayer.value >= 40 && !winTeam.heroRBDrafted) {
        winTeam.heroRBDrafted = true;
      }
      simDrafted.add(mockCurrentPlayer.name);
      simLog.push({
        pickNumber: simPick, player: mockCurrentPlayer, price: winPrice, winnerId,
        winnerName: simTeams[winnerId]?.name || `Team ${winnerId + 1}`,
        nominatorId: simNomIdx, nominatorName: simTeams[simNomIdx]?.name || `Team ${simNomIdx + 1}`,
        bidCount: willingBidders.length + 1,
      });
      simPick++;
      let next = (simNomIdx + 1) % settings.teams;
      let safety = 0;
      while (safety < settings.teams) { if (simTeams[next] && simTeams[next].roster.length < rosterSize) break; next = (next + 1) % settings.teams; safety++; }
      simNomIdx = next;
    }
    if (simDrafted.size < rosterSize * settings.teams) {
      const result = simulateDraftSync(simTeams, simPick, simNomIdx, simDrafted, simLog, mockUserSlot, settings.positions.SFLEX || 0, settings, dynamicPrices, null, rosterSize);
      simTeams = result.teams;
      simLog = result.draftLog;
      simDrafted = result.draftedPlayers;
      simPick = result.pickNumber;
    }
    setMockTeams(simTeams);
    setMockDraftLog(simLog);
    setMockDraftedPlayers(simDrafted);
    setMockPickNumber(simPick);
    setMockCurrentPlayer(null);
    setMockCurrentBid(1);
    setMockCurrentBidder(null);
    setMockActiveBidders(new Set());
    setMockSoldPlayer(null);
    setMockGoingPhase(null);
    SoundEngine.stopAmbiance();
    SoundEngine.stopFastForward();
    setMockPhase("complete");
    // Replay the headlessly-resolved tail into the recorder. Every live pick is
    // sale-logged by processSoldPlayer, which this path bypasses entirely, so
    // without this the stored record stops at the last pick the human played:
    // the results email (api/email/results.js) would send a roster missing the
    // tail, and scripts/draft_trends_report.py would lose exactly the late-round
    // range its "late value" classification reads.
    //
    // simLog already contains the picks the user played, so the tail is whatever
    // the recorder has not logged a sale for yet. Ask it by pick number rather
    // than by count: simLog's ordering is CONDITIONAL — oldest-first when the
    // branch above skipped simulateDraftSync, newest-first when it ran (that
    // function ends with draftLog.reverse()). The old slice(mockDraftLog.length)
    // assumed oldest-first unconditionally and so trimmed the wrong end, dropping
    // the last N picks of the draft and re-logging the first N, which were
    // already recorded. Each entry carries its own pickNumber, continuing from
    // where the user stopped — picks already played are never renumbered. Sales
    // only: no nominate/bid/dropout is invented for a tail that never had any,
    // and no price or winner is fabricated (both come resolved from the sim).
    selectTailSales(simLog, recordedSalePicks(draftLogRef.current)).forEach((entry) => {
      draftLogRef.current?.sale({
        pick: entry.pickNumber,
        player: entry.player,
        winnerSeat: entry.winnerId,
        price: entry.price,
      });
    });
    // Auto-finish/Simulate-to-End also ends the draft. Finalize with a distinct
    // status: the record now holds every pick, so the status is what
    // distinguishes a simulated tail from a fully-played draft.
    finalizeAndPersistLog("simulated_to_end");
    setMockAutopick(true);
    setUserSeatAiDriven(true); // the simulated tail drafts the user's seat
    localStorage.removeItem("auctionArchitect_draftState");
    captureMockDraftCompleted({
      picks: simDrafted.size,
      viaSimulate: true,
      durationSec: mockDraftStartedAtRef.current ? Math.round((Date.now() - mockDraftStartedAtRef.current) / 1000) : null,
    });
  }, [mockTeams, mockDraftedPlayers, mockDraftLog, mockPickNumber, mockNominatorIndex,
      mockPhase, mockCurrentPlayer, mockCurrentBid, mockCurrentBidder, mockActiveBidders, mockUserSlot]);

  // ── Auction loop effects ──────────────────────────────────────────────────

  // The shared idle gate for every loop effect below. mockPaused is the ⏸
  // button; the rest are overlays that own the screen while they're up, so the
  // auction must not advance underneath them. One expression, one mechanism —
  // every new overlay joins here rather than getting its own gate.
  //
  //   tutorialActive  — guided tutorial (the original precedent, a hook param)
  //   mockLeaveTarget — "Draft in Progress" leave-confirm
  //   settingsOpen    — Settings modal, which onOpenSettings can open over a
  //                     live draft ungated; the longest exposure of the three,
  //                     since Settings is a surface people linger in
  //   mockShowRecap   — mid-draft Recap modal, opened from the Recap button in
  //                     the mock controls bar, which renders under exactly the
  //                     mockDraftLive condition
  //
  // mockExportText is deliberately absent: its only non-null setter is
  // ResultsScreen's onExportText, and ResultsScreen renders only at
  // mockPhase === "complete" — where mockDraftLive is false by definition, so
  // there is no loop left to idle.
  //
  // Deliberately NOT folded into mockDraftActive: that flag also drives the
  // on-screen mock UI and the mobile body-scroll lock (~line 621), so idling it
  // would UNMOUNT the draft behind the dialog instead of freezing it.
  //
  // Purely derived — nothing here sets mockPaused, so the ⏸ button's own state
  // is untouched and "Stay" cannot accidentally resume a draft the user had
  // paused before opening the dialog. Closing an overlay restores the exact
  // prior state: React runs each effect's cleanup when the gate closes (which
  // clears the pending nomination / bid / going-phase timers) and re-runs it
  // against unchanged state when the gate opens, so nothing double-fires and no
  // countdown is skipped.
  const loopIdle = mockPaused || tutorialActive || mockLeaveTarget !== null
    || settingsOpen || mockShowRecap;

  // Effect 1: Nomination Timer Tick — 1-second interval for nomination countdown only
  useEffect(() => {
    if (appMode !== "mock" || loopIdle) {
      if (mockTickRef.current) { clearInterval(mockTickRef.current); mockTickRef.current = null; }
      return;
    }
    const needsTick = mockPhase === "nominating" && mockNomTimer !== null && mockNomTimer > 0;
    if (!needsTick) {
      if (mockTickRef.current) { clearInterval(mockTickRef.current); mockTickRef.current = null; }
      return;
    }
    if (mockTickRef.current) return;
    mockTickRef.current = setInterval(() => {
      setMockNomTimer(prev => prev !== null && prev > 0 ? prev - 1 : prev);
    }, 1000);
    return () => { if (mockTickRef.current) { clearInterval(mockTickRef.current); mockTickRef.current = null; } };
  }, [appMode, mockPhase, loopIdle, mockNomTimer]);

  // Effect 2: Nomination Logic — AI nomination + user timer init + timer expiry auto-nom
  useEffect(() => {
    if (appMode !== "mock" || mockPhase !== "nominating" || loopIdle) return;
    const isUserTurn = mockNominatorIndex === mockUserSlot && !mockAutopick;

    // ── Nomination recovery (shared by the user-seat and AI paths) ───────────
    // Both paths used to end in `if (pick) { nominate }` with no else. When the
    // nominator produced nothing, no state was written, no dep changed, and the
    // effect never re-ran — the UI sat on "nominating" forever and the draft was
    // unrecoverable without a reload. The headless paths (simulation.js:517,
    // liveLoopSim.js:98) have always had this recovery; this one did not, which
    // is why status=simulated_to_end appears in every format but status=completed
    // has only ever been recorded in 10- and 12-team 1QB.

    // The one terminal path in this effect — mirrored by the empty-pool guard
    // below and by skipNominator's two dead ends, so all three end a draft
    // identically.
    const completeDraftFromNomination = () => {
      setMockPhase("complete");
      finalizeAndPersistLog("completed");
      localStorage.removeItem("auctionArchitect_draftState");
      captureMockDraftCompleted({
        picks: mockDraftedPlayers.size,
        viaSimulate: false,
        durationSec: mockDraftStartedAtRef.current ? Math.round((Date.now() - mockDraftStartedAtRef.current) / 1000) : null,
      });
    };

    // Bounded per draft: a degenerate late draft could otherwise fire on every
    // remaining pick. seq keeps the truncation visible in PostHog.
    const reportFallback = (tier, diag) => {
      nomFallbackCountRef.current += 1;
      if (nomFallbackCountRef.current > NOM_FALLBACK_EVENT_CAP) return;
      captureNominationFallback({
        tier,
        seq: nomFallbackCountRef.current,
        pickNumber: mockPickNumber,
        teams: settings.teams,
        rosterSize,
        sflex: settings.positions?.SFLEX || 0,
        isUserSeat: isUserTurn,
        ...diag,
      });
    };

    // Deliberately NOT advanceToNextNom (:1183). That one is correct after a
    // SALE and wrong here, twice: it increments mockPickNumber (nothing sold, so
    // the pick number must not move — advancing it writes a pick number with no
    // sale attached, the exact signature hangdiag reports as a dangling
    // nomination), and its setMockPhase("nominating") is a no-op here because the
    // phase already IS "nominating" and React bails out on identical state.
    // mockNominatorIndex is the only dep that reliably re-triggers this effect,
    // so that is what moves.
    const skipNominator = (diag) => {
      // A full lap of skips without a single successful nomination means no team
      // can nominate anything from what is left. Advancing again would live-lock
      // the loop instead of freezing it — no better for the user. End the draft.
      if (nomSkipStreakRef.current >= settings.teams) {
        reportFallback("complete_skip_lap", diag);
        completeDraftFromNomination();
        return;
      }
      // Search the OTHER seats only (i starts at 1, stops before wrapping back
      // onto self) so "no other eligible nominator" comes back as null rather
      // than as our own index.
      let next = null;
      for (let i = 1; i < settings.teams; i++) {
        const cand = (mockNominatorIndex + i) % settings.teams;
        const t = mockTeams[cand];
        if (t && t.roster.length < rosterSize) { next = cand; break; }
      }
      // Wrap-around — the case that actually bites late. Setting mockNominatorIndex
      // to the value it already holds is not a state change, so advancing onto
      // ourselves would leave the draft frozen and this whole fix would silently
      // do nothing in exactly the situation it exists for.
      if (next === null) {
        reportFallback("complete_no_eligible", diag);
        completeDraftFromNomination();
        return;
      }
      reportFallback("skip_advance", diag);
      nomSkipStreakRef.current += 1;
      // Nothing was nominated, so mockPickNumber does NOT advance. The clock
      // resets so a skipped user seat gets a fresh one on the next lap (and so a
      // still-expired mockNomTimer === 0 can't instantly re-fire this path).
      setMockNomTimer(null);
      setMockNominatorIndex(next);
    };

    if (isUserTurn) {
      // Skip-to-my-pick: user's turn to nominate — stop skip and freeze
      if (skipModeRef.current) { stopSkipMode(); return; }
      SoundEngine.stopFastForward();
      mockUserPassedRef.current = false;
      setMockUserPassed(false);
      // null = no clock: never start the countdown, never auto-nominate.
      const userSec = getNomSeconds();
      if (userSec !== null && mockNomTimer === null) {
        setMockNomTimer(userSec);
      }
      if (userSec !== null && mockNomTimer === 0) {
        // Clock expiry enforces a deadline, not a veto: if the user already picked
        // someone, nominate THAT player at $1 (the price the NOMINATE panel preselects).
        // Deliberately not run through the K/DEF/QB/TE filters below — those keep AI
        // nominations sensible and must not silently override a deliberate human choice.
        if (mockNominatingPlayer && !mockDraftedPlayers.has(mockNominatingPlayer.name)) {
          mockNominatePlayer(mockNominatingPlayer, 1);
          return;
        }
        const userTeam = mockTeams[mockUserSlot];
        const uPosCounts = {};
        if (userTeam) userTeam.roster.forEach(p => { uPosCounts[p.player.pos] = (uPosCounts[p.player.pos] || 0) + 1; });
        let available = UNIQUE_PLAYERS.filter(p => !mockDraftedPlayers.has(p.name) && rosterable.has(p.pos))
          .filter(p => {
            // The K: 0 check that used to sit here is now the rosterable filter
            // above — it covers DEF too, so clock expiry can't auto-nominate the
            // user a defense in a league that doesn't roster one.
            if (p.pos === "K" && (uPosCounts.K || 0) >= 1) return false;
            if (p.pos === "DEF" && (uPosCounts.DEF || 0) >= (userTeam?.maxDef || 1)) return false;
            if (p.pos === "QB" && (uPosCounts.QB || 0) >= (userTeam?.maxQB || 2)) return false;
            if (p.pos === "TE" && (uPosCounts.TE || 0) >= (userTeam?.maxTE || 2)) return false;
            return true;
          });
        // Held for the recovery chain below: the position-legal pool before the
        // opening-round filters narrow it (the AI path's `available` vs nomPool).
        const uBasePool = available;
        if (mockPickNumber <= settings.teams) {
          const earlyPosCounts = {};
          mockDraftLog.forEach(l => { earlyPosCounts[l.player.pos] = (earlyPosCounts[l.player.pos] || 0) + 1; });
          const picksLeft = settings.teams - mockPickNumber + 1;
          if ((earlyPosCounts.QB || 0) >= 2) available = available.filter(p => p.pos !== "QB");
          if ((earlyPosCounts.TE || 0) >= 1) available = available.filter(p => p.pos !== "TE");
          const forcePos = new Set();
          if (5 - (earlyPosCounts.RB || 0) >= picksLeft) forcePos.add("RB");
          if (2 - (earlyPosCounts.WR || 0) >= picksLeft) forcePos.add("WR");
          if (forcePos.size > 0 && available.some(p => forcePos.has(p.pos))) {
            available = available.filter(p => forcePos.has(p.pos));
          }
        }
        const userProfile = userTeam?.profile;
        const uDiag = {
          posCounts: uPosCounts,
          nomPoolLen: available.length,
          availableLen: uBasePool.length,
          allAvailableLen: UNIQUE_PLAYERS.length - mockDraftedPlayers.size,
        };
        let best;
        if (userProfile) {
          best = pickAiNomination(userProfile, userTeam.roster, available, mockPickNumber, userTeam, mockTeams, settings.positions.SFLEX || 0, settings, rosterSize);
        }
        // Same dead end doNominate had, one tier deeper: this path already fell
        // back to the pool's best player, but an EMPTY pool left `best` undefined
        // and the `if (best)` below wrote no state — the clock stays expired, no
        // dep changes, the draft freezes. Same recovery, same terminal case.
        if (!best) {
          // Only a nominator that actually ran and came back empty is a fallback;
          // a seat with no profile never called it.
          if (userProfile) reportFallback("nom_pool_top", uDiag);
          best = [...available].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
        }
        if (!best) {
          reportFallback("available_top", uDiag);
          best = [...uBasePool].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
        }
        if (!best) { skipNominator(uDiag); return; }
        if (userProfile) {
          const isMid = mockPickNumber > 50 && mockPickNumber <= 80;
          const isSelfBuy = mockPickNumber > 80 ? true : Math.random() < (userProfile.selfNomRate + (isMid ? 0.2 : 0));
          const openBid = calcAiOpeningBid(userProfile, best, userTeam.roster, userTeam.budget,
                           rosterSize - userTeam.roster.length, isSelfBuy, userTeam, settings.positions.SFLEX || 0, settings, dynamicPrices, rosterSize);
          mockNominatePlayer(best, openBid);
        } else {
          mockNominatePlayer(best, 1);
        }
      }
      return;
    }

    // AI turn: random delay within aiNomSec range
    const team = mockTeams[mockNominatorIndex];
    if (!team) return;
    const profile = team.profile;
    if (!profile) return;
    // Positions the league does not roster are excluded at the SOURCE, so every
    // pool derived below inherits it: `available`, buildNomPool()'s nomPool, and
    // both fallback tiers in doNominate.
    //
    // pickAiNomination already applies isDraftable() internally (auction.js:1210),
    // which returns zero draftable defenses in a DEF: 0 league — so the AI's own
    // nominator was never the leak. The unguarded paths were the fallback tiers
    // added by the nomination-freeze hotfix, which read these pools directly, and
    // pickAiNomination's own `if (draftablePool.length > 0)` escape hatch, which
    // hands back the UNFILTERED pool when nothing draftable is left. Filtering
    // here closes both without touching the engine.
    //
    // Note the per-team caps below gate on ROSTER COUNTS (a team with zero DEF
    // passes `>= maxDef` in a DEF: 0 league), which is why they never caught this.
    const allAvailable = UNIQUE_PLAYERS.filter(p => !mockDraftedPlayers.has(p.name) && rosterable.has(p.pos));
    // NOTE: this is the pool before the per-team position caps. The capped
    // `available` below and buildNomPool()'s nomPool can both be empty while
    // this still holds hundreds of players — that is the freeze case, and it is
    // handled in doNominate, not here.
    if (allAvailable.length === 0) {
      completeDraftFromNomination();
      return;
    }
    const tPosCounts = {};
    team.roster.forEach(p => { tPosCounts[p.player.pos] = (tPosCounts[p.player.pos] || 0) + 1; });
    const available = allAvailable.filter(p => {
      if (p.pos === "K" && (tPosCounts.K || 0) >= 1) return false;
      if (p.pos === "DEF" && (tPosCounts.DEF || 0) >= (team.maxDef || 1)) return false;
      if (p.pos === "QB" && (tPosCounts.QB || 0) >= (team.maxQB || 2)) return false;
      if (p.pos === "TE" && (tPosCounts.TE || 0) >= (team.maxTE || 2)) return false;
      return true;
    });

    // Build nomination pool (shared by both skip and normal paths)
    const buildNomPool = () => {
      let nomPool = available;
      if (mockPickNumber <= settings.teams) {
        const earlyPosCounts = {};
        mockDraftLog.forEach(l => { earlyPosCounts[l.player.pos] = (earlyPosCounts[l.player.pos] || 0) + 1; });
        const picksLeft = settings.teams - mockPickNumber + 1;
        if ((earlyPosCounts.QB || 0) >= 2) nomPool = nomPool.filter(p => p.pos !== "QB");
        if ((earlyPosCounts.TE || 0) >= 1) nomPool = nomPool.filter(p => p.pos !== "TE");
        const forcePos = new Set();
        if (5 - (earlyPosCounts.RB || 0) >= picksLeft) forcePos.add("RB");
        if (2 - (earlyPosCounts.WR || 0) >= picksLeft) forcePos.add("WR");
        if (forcePos.size > 0 && nomPool.some(p => forcePos.has(p.pos))) {
          nomPool = nomPool.filter(p => forcePos.has(p.pos));
        }
      }
      return nomPool;
    };

    const doNominate = () => {
      const nomPool = buildNomPool();
      const diag = {
        posCounts: tPosCounts,
        nomPoolLen: nomPool.length,
        availableLen: available.length,
        allAvailableLen: allAvailable.length,
      };
      let pick = pickAiNomination(profile, team.roster, nomPool, mockPickNumber, team, mockTeams, settings.positions.SFLEX || 0, settings, rosterSize);
      // Mirrors simulation.js:517 / liveLoopSim.js:98 — the nominator returning
      // nothing must never be terminal for the draft. Recover inside the
      // nomination pool first, then the position-filtered pool, then move the
      // nominator on. Copy before sorting: those two sort in place, which is safe
      // on their own local arrays but not on one derived from React state.
      if (!pick) {
        reportFallback("nom_pool_top", diag);
        pick = [...nomPool].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
      }
      if (!pick) {
        reportFallback("available_top", diag);
        pick = [...available].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
      }
      if (!pick) { skipNominator(diag); return; }
      const isMid = mockPickNumber > 50 && mockPickNumber <= 80;
      const isSelfBuy = mockPickNumber > 80 ? true : Math.random() < (profile.selfNomRate + (isMid ? 0.2 : 0));
      const openBid = calcAiOpeningBid(profile, pick, team.roster, team.budget,
                       rosterSize - team.roster.length, isSelfBuy, team, settings.positions.SFLEX || 0, settings, dynamicPrices, rosterSize);
      mockNominatePlayer(pick, openBid);
    };

    const [minSec, maxSec] = SPEED_SETTINGS[mockSpeed]?.aiNomSec || [2, 4];
    const delayMs = (minSec + Math.random() * (maxSec - minSec)) * 1000;
    const finalNomDelay = skipModeRef.current ? 0 : mockUserPassedRef.current ? Math.max(delayMs * 0.3, 200) : delayMs;

    if (mockNomTimerRef.current) clearTimeout(mockNomTimerRef.current);
    mockNomTimerRef.current = setTimeout(doNominate, finalNomDelay);
    return () => { if (mockNomTimerRef.current) clearTimeout(mockNomTimerRef.current); };
  }, [appMode, mockPhase, mockNominatorIndex, mockUserSlot, mockTeams, mockDraftedPlayers,
      mockPickNumber, mockSpeed, mockNominatePlayer, loopIdle, mockAutopick, mockNomTimer,
      getNomSeconds, mockNominatingPlayer]);

  // Effect 2b: Nomination watchdog ───────────────────────────────────────────
  // Effect 2 has three silent returns that write no state — the loopIdle gate
  // (:1503) and the !team / !profile guards (:1681, :1683). A silent return
  // changes no dep, so the effect never re-runs and the draft sits on
  // "nominating" forever. That is the shape of 91 abandoned drafts across 16
  // builds: ends on a SALE with picks left, median 54s of dead air, tail past
  // ten minutes.
  //
  // Arming/idempotence: every value that a live draft changes is in the dep
  // array, so ANY progress tears the timer down via cleanup and re-arms a new
  // one. A timer that survives the full delay is therefore proof that nothing
  // moved — no need to re-read state inside the callback, which could not see
  // current values anyway. mockNomTimer is included deliberately: a user seat
  // counting down re-arms this every tick and can never trip it, while a frozen
  // draft has no tick (Effect 1 stops ticking under loopIdle) and does.
  //
  // firedRef keys the log to a specific pick so a long legitimate pause logs
  // once, not every 45s.
  const nomWatchdogFiredRef = useRef(null);
  useEffect(() => {
    if (appMode !== "mock" || !mockDraftLive) return;
    if (mockPhase !== "nominating" && mockPhase !== "bidding") return;
    // Key the fire-once guard by phase as well as pick: one pick legitimately
    // passes through both phases, and a pick-only key would let the nominating
    // fire suppress the bidding one for the same pick.
    const armedPick = mockPickNumber;
    const armedKey = `${mockPhase}:${armedPick}`;
    const t = setTimeout(() => {
      const team = mockTeams[mockNominatorIndex];
      const available = UNIQUE_PLAYERS.filter(p => !mockDraftedPlayers.has(p.name) && rosterable.has(p.pos));
      // Force-advance ONLY when the loop was not deliberately idle. When a
      // loopIdle flag is set the draft is paused by design and the user has a
      // Resume affordance; advancing through a pause the user asked for would
      // be a worse bug than the freeze. Those fires are logged, not rescued —
      // and the log is what names the offending flag.
      const idle = loopIdle;
      // Bidding is LOG-ONLY. There is no safe forced advance mid-auction: the
      // only way "on" is to resolve the sale, and resolving one the loop never
      // finished would hand a player to the wrong team at the wrong price —
      // worse than the stall. The corpus scan found more long-dwell stalls in
      // bidding than in nomination (33 vs 13 over 300s), so this half needs the
      // diagnosis even though it cannot take the rescue.
      const canAdvance = !idle && mockPhase === "nominating";
      if (nomWatchdogFiredRef.current !== armedKey) {
        nomWatchdogFiredRef.current = armedKey;
        console.error("[nomination-watchdog]", {
          pick: armedPick, phase: mockPhase, nominator: mockNominatorIndex,
          nominatorName: team?.name ?? null, mockPaused, tutorialActive,
          mockLeaveTarget, settingsOpen, mockShowRecap,
          availableLen: available.length, teamsLen: Object.keys(mockTeams).length,
          nominee: mockNominatingPlayer?.name ?? null, willAdvance: canAdvance,
          // Bidding context — what the auction was sitting on when it died.
          currentPlayer: mockCurrentPlayer?.name ?? null, currentBid: mockCurrentBid,
          currentBidder: mockCurrentBidder, goingPhase: mockGoingPhase,
          activeBidders: mockActiveBidders?.size ?? null,
        });
        captureNominationWatchdog({
          pickNumber: armedPick, phase: mockPhase, nominatorIndex: mockNominatorIndex,
          nominatorName: team?.name ?? null,
          isUserSeat: mockNominatorIndex === mockUserSlot && !mockAutopick,
          waitedMs: NOM_WATCHDOG_MS, advanced: canAdvance,
          mockPaused, tutorialActive, mockLeaveTarget, settingsOpen, mockShowRecap,
          availableLen: available.length, teamsLen: Object.keys(mockTeams).length,
          nominee: mockNominatingPlayer?.name ?? null,
          nomineeAlreadyDrafted: mockNominatingPlayer
            ? mockDraftedPlayers.has(mockNominatingPlayer.name) : false,
        });
      }
      if (!canAdvance) return;
      // Recovery mirrors skipNominator: move the nominator on and let Effect 2
      // run its normal path. This never nominates anything itself, so even a
      // spurious fire cannot double-nominate — and mockPickNumber does not
      // move, because nothing sold.
      let next = null;
      for (let i = 1; i < settings.teams; i++) {
        const cand = (mockNominatorIndex + i) % settings.teams;
        const c = mockTeams[cand];
        if (c && c.roster.length < rosterSize) { next = cand; break; }
      }
      if (next === null) return;
      setMockNomTimer(null);
      setMockNominatorIndex(next);
    }, NOM_WATCHDOG_MS);
    return () => clearTimeout(t);
    // The bidding-side deps (currentPlayer/bid/bidder/goingPhase/activeBidders)
    // are what make a live auction re-arm this instead of tripping it: every
    // bid, dropout and going-phase step tears the timer down. A timer that
    // survives the full delay in "bidding" means no bid, no dropout and no
    // going-phase step happened for 45s, which no live auction does.
  }, [appMode, mockPhase, mockDraftLive, mockPickNumber, mockNominatorIndex, mockTeams,
      mockDraftedPlayers, mockNomTimer, mockNominatingPlayer, loopIdle, mockPaused,
      tutorialActive, mockLeaveTarget, settingsOpen, mockShowRecap, rosterable,
      rosterSize, settings.teams, mockUserSlot, mockAutopick,
      mockCurrentPlayer, mockCurrentBid, mockCurrentBidder, mockGoingPhase, mockActiveBidders]);

  // Effect 3: AI Bidding — random delays + jump-bid logic
  useEffect(() => {
    if (appMode !== "mock" || mockPhase !== "bidding" || !mockCurrentPlayer || loopIdle) return;
    if (mockGoingPhase === "sold") return;
    const allTeamsAtMinimum = Object.values(mockTeams).every(t => {
      const slots = rosterSize - t.roster.length;
      return slots <= 0 || t.budget <= slots;
    });
    if (allTeamsAtMinimum) {
      processSoldPlayer(mockCurrentPlayer, 1, mockCurrentBidder);
      return;
    }
    const eliteCount = mockDraftLog.filter(l => l.price >= 50).length;
    const willingBidders = [];
    mockActiveBidders.forEach(id => {
      if (id === mockUserSlot && !mockAutopick) return;
      if (id === mockCurrentBidder) return;
      const team = mockTeams[id];
      if (!team) return;
      const profile = team.profile;
      if (!profile) return;
      const emptySlots = rosterSize - team.roster.length;
      const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
      const maxBid = calcAiMaxBidV2(profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount, teamCaps, team, mockPickNumber, mockTierAnchorsRef.current, mockInflationRef.current, mockTeams, settings.positions.SFLEX || 0, settings, dynamicPrices, mockDraftedPlayers, mockNominatorIndex, rosterSize);
      if (maxBid > mockCurrentBid) willingBidders.push({ id, maxBid });
    });
    // Enforcer logic: bid up undervalued players toward fair value
    if (mockCurrentPlayer.value >= 20 && mockCurrentBid < mockCurrentPlayer.value * 0.60) {
      Object.entries(mockTeams).forEach(([id, team]) => {
        const i = parseInt(id);
        if (i === mockUserSlot && !mockAutopick) return;
        if (i === mockCurrentBidder) return;
        const profile = team.profile;
        if (!profile || !profile.enforcer) return;
        if (profile.starsMode) {
          const sPicks = team.roster.filter(p => p.price >= 40);
          if (sPicks.length >= 3) return;
          const sPosCounts = {};
          sPicks.forEach(p => { sPosCounts[p.player.pos] = (sPosCounts[p.player.pos] || 0) + 1; });
          if ((sPosCounts[mockCurrentPlayer.pos] || 0) >= 2) return;
        }
        const emptySlots = rosterSize - team.roster.length;
        if (emptySlots <= 0 || team.budget < mockCurrentBid + 1) return;
        const hardCap = emptySlots <= 1 ? team.budget : team.budget - (emptySlots - 1);
        const eBudget = team.budget;
        const budgetFactor = eBudget > 100 ? 1.0 : eBudget >= 60 ? 0.7 : eBudget >= 30 ? 0.3 : 0.0;
        if (budgetFactor === 0) return;
        const eStarterReqs = { QB: 1, RB: 2, WR: 2, TE: 1, DEF: 1, K: 1 };
        Object.entries(profile.starterOverrides || {}).forEach(([p, c]) => { eStarterReqs[p] = c; });
        const ePosCnts = {};
        team.roster.forEach(r => { ePosCnts[r.player.pos] = (ePosCnts[r.player.pos] || 0) + 1; });
        let openStarters = 0;
        for (const [p, req] of Object.entries(eStarterReqs)) {
          openStarters += Math.max(0, req - (ePosCnts[p] || 0));
        }
        const slotsFactor = openStarters >= 4 ? 1.0 : openStarters >= 2 ? 0.5 : 0.1;
        const baseRate = 0.75;
        const enforcementWillingness = baseRate * budgetFactor * slotsFactor;
        if (Math.random() > enforcementWillingness) return;
        let phaseMaxBid = Infinity;
        if (mockPickNumber > 70) phaseMaxBid = 3;
        else if (mockPickNumber > 30) phaseMaxBid = Math.round(mockCurrentPlayer.value * 0.45);
        let reserveForTargets = 0;
        const ePosSpent = {};
        team.roster.forEach(r => { ePosSpent[r.player.pos] = (ePosSpent[r.player.pos] || 0) + r.price; });
        if (profile.shoppingList) {
          for (const [slotKey, slotDef] of Object.entries(profile.shoppingList)) {
            const slotPos = slotDef.pos || slotKey.replace(/[0-9]/g, '');
            if (slotPos === "ANY" || slotPos === "STAR") continue;
            const slotNum = parseInt(slotKey.replace(/[A-Z]+/g, ''), 10) || 1;
            const filled = ePosCnts[slotPos] || 0;
            if (slotKey === "FLEX") {
              const starterCount = eStarterReqs[slotPos] || 2;
              if (filled > starterCount) continue;
            } else if (filled >= slotNum) continue;
            reserveForTargets += slotDef.maxBid[0];
          }
        }
        const filledSlots = team.roster.length;
        const totalStarters = Object.values(eStarterReqs).reduce((s, v) => s + v, 0);
        const benchSlots = Math.max(0, rosterSize - totalStarters);
        const filledBench = Math.max(0, filledSlots - totalStarters);
        reserveForTargets += Math.max(0, benchSlots - filledBench);
        const reserveCap = Math.max(0, eBudget - reserveForTargets);
        const enforcerMax = mockEnforcerCaught
          ? Math.round(mockCurrentPlayer.value * 0.72)
          : Math.round(mockCurrentPlayer.value * 0.55);
        let maxBid = Math.min(enforcerMax, hardCap, phaseMaxBid, reserveCap);
        const ePos = mockCurrentPlayer.pos;
        if (profile.heroRB && ePos === "RB" && team.heroRBDrafted) maxBid = Math.min(maxBid, 5);
        if (profile.rbCap < 999 && ePos === "RB") maxBid = Math.min(maxBid, profile.rbCap);
        if (profile.rbSpendCap > 0 && ePos === "RB") {
          const eRbSpent = team.roster.filter(p => p.player.pos === "RB").reduce((s, p) => s + p.price, 0);
          if (eRbSpent >= profile.rbSpendCap) maxBid = 0;
          else maxBid = Math.min(maxBid, profile.rbSpendCap - eRbSpent);
        }
        const ePosKey = (ePos === "DEF" || ePos === "K") ? null : ePos.toLowerCase() + "$";
        if (profile.favoriteTeam && mockCurrentPlayer.team === profile.favoriteTeam && ePosKey) {
          const eSpent = team.roster.filter(p => p.player.pos === ePos).reduce((s, p) => s + p.price, 0);
          const eTarget = profile[ePosKey] || 0;
          if (eTarget > 0 && eSpent >= eTarget + 10) maxBid = 0;
          else if (eTarget > 0) maxBid = Math.min(maxBid, Math.max(0, eTarget + 10 - eSpent));
        }
        if (profile.archetype === "Balanced") maxBid = Math.min(maxBid, 40);
        if (maxBid <= mockCurrentBid) return;
        const existing = willingBidders.find(wb => wb.id === i);
        if (existing) {
          if (maxBid > existing.maxBid) existing.maxBid = maxBid;
        } else {
          willingBidders.push({ id: i, maxBid });
        }
      });
    }
    applyValueRecognitionFloor({
      player: mockCurrentPlayer, currentBid: mockCurrentBid,
      excludeIndex: mockCurrentBidder, skipIndex: mockAutopick ? -1 : mockUserSlot,
      willingBidders, teams: mockTeams, rosterSize, priceMap: dynamicPrices, budget: settings?.budget ?? 200,
      recheckMaxBid: (team, emptySlots, teamCaps) => calcAiMaxBidV2(
        team.profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount,
        teamCaps, team, mockPickNumber, mockTierAnchorsRef.current, mockInflationRef.current,
        mockTeams, settings.positions.SFLEX || 0, settings, dynamicPrices,
        mockDraftedPlayers, mockNominatorIndex, rosterSize),
    });
    const userManual = mockActiveBidders.has(mockUserSlot) && !mockAutopick;
    if ((mockSpeed === "instant" || skipModeRef.current) && !userManual) {
      if (willingBidders.length === 0) {
        bidCountRef.current = 1;
        processSoldPlayer(mockCurrentPlayer, mockCurrentBid, mockCurrentBidder);
        return;
      }
      bidCountRef.current = willingBidders.length + 1;
      let highestMax = 0;
      let highestId = willingBidders[0].id;
      let secondMax = mockCurrentBid;
      willingBidders.forEach(({ id, maxBid }) => {
        if (maxBid > highestMax) {
          secondMax = Math.max(secondMax, highestMax);
          highestMax = maxBid;
          highestId = id;
        } else if (maxBid > secondMax) {
          secondMax = maxBid;
        }
      });
      const finalPrice = Math.max(mockCurrentBid, secondMax + 1);
      processSoldPlayer(mockCurrentPlayer, Math.min(finalPrice, highestMax), highestId);
      return;
    }
    if (willingBidders.length === 0) return;
    // Randomised order rather than a single random pick. calcAiMaxBidV2 is
    // stochastic, so the selected bidder can re-sample below the current bid and
    // decline (see the resample below); when that happened the effect returned
    // without changing state, so it never re-ran and Effect 4 sold the player —
    // one unlucky draw ended the auction for the whole room. The floor makes it
    // worse by adding teams whose raw max is exactly $0, which must decline.
    // Measured: every under-half clear ended this way, with a mean of 6.7 teams
    // still valuing the player above the price it settled at. The pool is now
    // walked in order until someone bids.
    const bidderPool = [...willingBidders];
    for (let i = bidderPool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bidderPool[i], bidderPool[j]] = [bidderPool[j], bidderPool[i]];
    }
    const [minMs, maxMs] = SPEED_SETTINGS[mockSpeed]?.aiBidMs || [1000, 4000];
    const delayMs = minMs + Math.random() * (maxMs - minMs);
    const finalDelay = skipModeRef.current ? 0 : mockUserPassedRef.current ? Math.max(delayMs * 0.2, 100) : delayMs;
    if (mockBidTimerRef.current) clearTimeout(mockBidTimerRef.current);
    bidPendingRef.current = true;
    mockBidTimerRef.current = setTimeout(() => {
      // Fired — no longer "scheduled and unfired". Released BEFORE any early
      // return below, so a bidder that declines can never wedge the auction
      // open waiting on a decision that has already been made.
      bidPendingRef.current = false;
      // Walk the shuffled pool until one team actually bids. Resolved inside
      // this single timer, so a decline costs no extra wall-clock delay: one
      // timer firing still yields at most one accepted bid and the going-chain
      // timing is untouched. Terminates because the pool is a fixed-length
      // array walked once, and every accepted bid strictly raises
      // mockCurrentBid against a finite budget, so the effect cannot re-run
      // forever either.
      let placedId = null;
      let newBid = 0;
      for (const cand of bidderPool) {
        const team = mockTeams[cand.id];
        if (!team) continue;
        const profile = team.profile;
        if (!profile) continue;
        const emptySlots = rosterSize - team.roster.length;
        const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
        const maxBid = calcAiMaxBidV2(profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount, teamCaps, team, mockPickNumber, mockTierAnchorsRef.current, mockInflationRef.current, mockTeams, settings.positions.SFLEX || 0, settings, dynamicPrices, mockDraftedPlayers, mockNominatorIndex, rosterSize);
        if (maxBid <= mockCurrentBid) continue;   // declined — ask the next one
        let increment = 1;
        const gap = maxBid - mockCurrentBid;
        if (gap >= 15 && Math.random() < 0.35) {
          increment = Math.min(3 + Math.floor(Math.random() * 3), gap);
        }
        const teamBudget = team.budget || 0;
        const teamSlots = rosterSize - (team.roster?.length || 0);
        const absoluteMax = teamSlots <= 1 ? teamBudget : teamBudget - (teamSlots - 1);
        const candBid = Math.min(mockCurrentBid + increment, maxBid, absoluteMax);
        if (candBid <= mockCurrentBid) continue;
        placedId = cand.id;
        newBid = candBid;
        break;
      }
      // Every willing bidder declined on its own resample — fall through to the
      // going-chain and let the player sell, exactly as before.
      if (placedId === null) return;
      bidCountRef.current++;
      setMockCurrentBid(newBid);
      setMockCurrentBidder(placedId);
      setMockGoingPhase(null);
      if (mockSpeed !== "instant") playMockSound("bid");
      SoundEngine.setAmbianceIntensity(newBid / (mockCurrentPlayer?.value || 50));
      setMockActiveBidders(prev => {
        const next = new Set(prev);
        prev.forEach(id => {
          if (id === mockUserSlot && !mockAutopick) return;
          const t = mockTeams[id];
          if (!t) { next.delete(id); return; }
          const p = t.profile;
          if (!p) { next.delete(id); return; }
          const es = rosterSize - t.roster.length;
          const tc = { maxDef: t.maxDef, maxQB: t.maxQB, maxTE: t.maxTE };
          const mb = calcAiMaxBidV2(p, mockCurrentPlayer, t.roster, t.budget, es, eliteCount, tc, t, mockPickNumber, mockTierAnchorsRef.current, mockInflationRef.current, mockTeams, settings.positions.SFLEX || 0, settings, dynamicPrices, mockDraftedPlayers, mockNominatorIndex, rosterSize);
          if (mb <= newBid && id !== placedId) {
            next.delete(id);
          }
        });
        return next;
      });
    }, finalDelay);
    return () => {
      if (mockBidTimerRef.current) clearTimeout(mockBidTimerRef.current);
      // Cleared before firing: the effect re-runs and reschedules, so the
      // auction must not go on treating this as a decision already made.
      bidPendingRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mockGoingPhase intentionally excluded: including it causes phase transitions to cancel pending AI bids
  }, [appMode, mockPhase, mockCurrentPlayer, mockCurrentBid, mockCurrentBidder, mockActiveBidders, mockTeams, mockUserSlot, mockSpeed, processSoldPlayer, loopIdle, mockAutopick, mockDraftLog, mockEnforcerCaught]);

  // Effect 4: Going Once / Going Twice / Sold progression (+ last-second snipe bids)
  useEffect(() => {
    if (appMode !== "mock" || mockPhase !== "bidding" || !mockCurrentPlayer || loopIdle) return;
    const phaseSec = SPEED_SETTINGS[mockSpeed]?.phaseSec || 1.5;
    const phaseMs = phaseSec * 1000;
    const finalPhaseMs = skipModeRef.current ? 0 : mockUserPassedRef.current ? Math.max(phaseMs * 0.3, 200) : phaseMs;

    // trySnipeBid — find an AI team willing to bid above current price and schedule a last-second bid
    const trySnipeBid = () => {
      if (snipeCountRef.current >= 6 || snipesThisNomRef.current >= 2) return null;
      const userIsHighBidder = mockCurrentBidder === mockUserSlot;
      const threshold = userIsHighBidder ? 0.30 : 0.15;
      if (Math.random() > threshold) return null;
      const eliteCount = mockDraftLog.filter(l => l.price >= 50).length;
      const candidates = [];
      Object.entries(mockTeams).forEach(([id, team]) => {
        const i = parseInt(id);
        if (i === mockUserSlot && !mockAutopick) return;
        if (i === mockCurrentBidder) return;
        if (!team || !team.profile) return;
        const emptySlots = rosterSize - team.roster.length;
        if (emptySlots <= 0) return;
        const teamCaps = { maxDef: team.maxDef, maxQB: team.maxQB, maxTE: team.maxTE };
        const maxBid = calcAiMaxBidV2(team.profile, mockCurrentPlayer, team.roster, team.budget, emptySlots, eliteCount, teamCaps, team, mockPickNumber, mockTierAnchorsRef.current, mockInflationRef.current, mockTeams, settings.positions.SFLEX || 0, settings, dynamicPrices, mockDraftedPlayers, mockNominatorIndex, rosterSize);
        if (maxBid > mockCurrentBid) candidates.push({ id: i, maxBid });
      });
      if (candidates.length === 0) return null;
      const sniper = candidates[Math.floor(Math.random() * candidates.length)];
      snipePendingRef.current = true;
      const delayMs = 500 + Math.random() * 1000;
      return setTimeout(() => {
        snipePendingRef.current = false;
        if (sniper.maxBid <= mockCurrentBid) return;
        let increment = 1;
        const gap = sniper.maxBid - mockCurrentBid;
        if (gap >= 15 && Math.random() < 0.35) {
          increment = Math.min(3 + Math.floor(Math.random() * 3), gap);
        }
        const team = mockTeams[sniper.id];
        if (!team) return;
        const teamSlots = rosterSize - team.roster.length;
        const absoluteMax = teamSlots <= 1 ? team.budget : team.budget - (teamSlots - 1);
        const newBid = Math.min(mockCurrentBid + increment, sniper.maxBid, absoluteMax);
        if (newBid <= mockCurrentBid) return;
        setMockCurrentBid(newBid);
        setMockCurrentBidder(sniper.id);
        setMockGoingPhase(null);
        playMockSound("bid");
        SoundEngine.setAmbianceIntensity(newBid / (mockCurrentPlayer?.value || 50));
        bidCountRef.current++;
        snipeCountRef.current++;
        snipesThisNomRef.current++;
      }, delayMs);
    };

    const canSnipe = !skipModeRef.current && mockSpeed !== "instant"
      && mockCurrentBid >= 5
      && mockCurrentPlayer.pos !== "DEF" && mockCurrentPlayer.pos !== "K"
      && !snipePendingRef.current;

    // Skip mode: Effect 3's instant second-price path handles bid resolution
    // and calls processSoldPlayer directly. Effect 4 only needs to handle
    // the case where Effect 3 didn't resolve (e.g. no willing bidders found
    // on a later re-render). Use 0ms delays for all phase transitions.
    if (mockGoingPhase === null) {
      const timer = setTimeout(() => setMockGoingPhase("once"), finalPhaseMs);
      return () => clearTimeout(timer);
    }
    if (mockGoingPhase === "once") {
      let snipeTimer = null;
      if (canSnipe) snipeTimer = trySnipeBid();
      const delay = mockUserPassedRef.current ? 0 : finalPhaseMs;
      const timer = setTimeout(() => setMockGoingPhase(mockUserPassedRef.current ? "sold" : "twice"), delay);
      return () => { clearTimeout(timer); if (snipeTimer) { clearTimeout(snipeTimer); snipePendingRef.current = false; } };
    }
    if (mockGoingPhase === "twice") {
      let snipeTimer = null;
      if (canSnipe) snipeTimer = trySnipeBid();
      const delay = mockUserPassedRef.current ? 0 : finalPhaseMs;
      const timer = setTimeout(() => setMockGoingPhase("sold"), delay);
      return () => { clearTimeout(timer); if (snipeTimer) { clearTimeout(snipeTimer); snipePendingRef.current = false; } };
    }
    if (mockGoingPhase === "sold") {
      if (snipePendingRef.current) return; // wait for pending snipe bid to resolve
      // ── AUCTION INVARIANT ────────────────────────────────────────────────
      // May not resolve while an AI bid is scheduled and unfired. The line
      // below used to cancel that bid outright, so one participant passing
      // ended the auction for the other eleven: the going phases collapse when
      // the user passes, reach "sold" first, and killed the pending bid. An
      // elite player opened at $1 sold at $1.
      //
      // Poll rather than returning bare. If the pending bidder DECLINES, no
      // state changes, this effect's deps never move again, and a bare return
      // would leave the nomination unresolved forever — trading a cheap sale
      // for a hang. The recheck always re-enters.
      if (bidPendingRef.current) {
        const recheck = setTimeout(() => setBidResolveTick(t => t + 1), BID_PENDING_RECHECK_MS);
        return () => clearTimeout(recheck);
      }
      // Reached only with no bid outstanding, so this can no longer cancel a
      // live decision — it just drops a stale id from an earlier nomination.
      if (mockBidTimerRef.current) { clearTimeout(mockBidTimerRef.current); mockBidTimerRef.current = null; }
      const soldMs = skipModeRef.current ? 0 : 600;
      const timer = setTimeout(() => {
        processSoldPlayer(mockCurrentPlayer, mockCurrentBid, mockCurrentBidder);
      }, soldMs);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- snipe logic reads mockTeams/mockDraftLog/etc. from closure; stable during going phases
  }, [appMode, mockPhase, mockCurrentPlayer, loopIdle, mockSpeed,
      mockGoingPhase, mockCurrentBid, mockCurrentBidder, processSoldPlayer, mockUserPassed,
      bidResolveTick]);

  // Effect 5: Sound effects on going phase changes + heartbeat when user is winning
  useEffect(() => {
    if (appMode !== "mock") return;
    if (mockGoingPhase === "once") playMockSound("goingOnce");
    else if (mockGoingPhase === "twice") playMockSound("goingTwice");
    else if (mockGoingPhase === "sold") playMockSound("sold");

    const userIsWinning = mockCurrentBidder === mockUserSlot;
    let soldStopTimer = null;

    if (mockGoingPhase === "once" && userIsWinning) {
      SoundEngine.startHeartbeat(60);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = setInterval(() => {
        setHeartbeatPulse(true);
        setTimeout(() => setHeartbeatPulse(false), 200);
      }, (60 / 60) * 1000);
    } else if (mockGoingPhase === "twice" && userIsWinning) {
      SoundEngine.setBPM(100);
    } else if (mockGoingPhase === "sold" && userIsWinning) {
      SoundEngine.setBPM(140);
      soldStopTimer = setTimeout(() => {
        SoundEngine.stopHeartbeat();
        if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
        setHeartbeatPulse(false);
      }, 500);
    } else {
      SoundEngine.stopHeartbeat();
      if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
      setHeartbeatPulse(false);
    }

    return () => { if (soldStopTimer) clearTimeout(soldStopTimer); };
  }, [appMode, mockGoingPhase, mockCurrentBidder, mockUserSlot, playMockSound]);

  // ── Return ─────────────────────────────────────────────────────────────────
  return {
    // State
    mockPhase, setMockPhase,
    mockUserOwner, setMockUserOwner,
    mockTeamConfigs, setMockTeamConfigs,
    // Draft-time view of the configs: profile overlay folded in when the league
    // is calibrated, identical to mockTeamConfigs otherwise. Callers that BUILD
    // a draft must use this; callers that EDIT configs must use the raw state.
    effectiveTeamConfigs, modeledSeatCount,
    mockUserSlot, setMockUserSlot,
    mockSpeed, setMockSpeed,
    mockPickNumber, setMockPickNumber,
    mockNominatorIndex, setMockNominatorIndex,
    mockDraftedPlayers, setMockDraftedPlayers,
    mockDraftLog, setMockDraftLog,
    mockKeeperPicks,
    mockTeams, setMockTeams,
    mockCurrentPlayer, setMockCurrentPlayer,
    mockCurrentBid, setMockCurrentBid,
    mockCurrentBidder, setMockCurrentBidder,
    mockActiveBidders, setMockActiveBidders,
    mockSoldPlayer, setMockSoldPlayer,
    mockSearch, setMockSearch,
    mockPosFilter, setMockPosFilter,
    mockMobileTab, setMockMobileTab,
    mockShowRecap, setMockShowRecap,
    mockPaused, setMockPaused,
    mockAutopick, setMockAutopick,
    userSeatAiDriven, setUserSeatAiDriven,
    mockNomTimer, setMockNomTimer,
    mockNomTimerMode, setMockNomTimerMode, getNomSeconds,
    mockGoingPhase, setMockGoingPhase,
    mockNominatingPlayer, setMockNominatingPlayer,
    mockOpeningBid, setMockOpeningBid,
    mockExpandedTeam, setMockExpandedTeam,
    mockWatchlist, setMockWatchlist,
    myAvoids, setMyAvoids,
    mockWatchlistAlert, setMockWatchlistAlert,
    mockNominateOverlay, setMockNominateOverlay,
    mockNomGlow, setMockNomGlow,
    heartbeatPulse, setHeartbeatPulse,
    showMockOverflow, setShowMockOverflow,
    mockSpeedToast, setMockSpeedToast,
    mockMuted, setMockMuted,
    mockLeaveTarget, setMockLeaveTarget,
    mockExportText, setMockExportText,
    mockSavedDraft, setMockSavedDraft,
    mockEnforcerCaught, setMockEnforcerCaught,
    skipMode, setSkipMode,
    // Refs
    mockSpeedToastRef,
    heartbeatIntervalRef,
    mockBidTimerRef,
    mockUserPassedRef,
    mockNomTimerRef,
    mockSoldTimerRef,
    mockTickRef,
    mockTierAnchorsRef,
    mockInflationRef,
    skipModeRef,
    mockMutedRef,
    // Exposed so the results screen can offer to email THIS draft: the id must
    // be the one under which the record was actually persisted to Upstash, not
    // a freshly minted one. Null on a resumed draft — resumeMockDraft never
    // recreates the recorder, so no record exists to email.
    draftLogRef,
    // Computed
    mockDraftActive,
    mockDraftLive,
    mockAvailablePlayers,
    mockUserTeam,
    mockIsUserTurn,
    mockUserMaxBid,
    // Actions
    playMockSound,
    startMockDraft,
    resetMockDraft,
    resumeMockDraft,
    mockNominatePlayer,
    processSoldPlayer,
    mockPlaceBid,
    mockPassBid,
    toggleMockPaused,
    toggleWatchlist,
    toggleAvoid,
    autoFinishMock,
    startSkip,
    stopSkipMode,
    clearHandoffPause,
  };
}
